import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { useToast } from '@/components/ui/use-toast';
import { Radio, ListMusic, Check, User, Timer, Edit, Info, Tag, Undo2, ArrowLeft, Copy, X, Music, MessageCircle, Heart, Users, Download, Loader2 } from 'lucide-react';
import QRCode from 'qrcode.react';

// ✅ IMPORT THE NEW FIREBASE AUTH CONTEXT AND SERVICES
import { useAuth } from '@/contexts/FirebaseAuthContext';
import { useCurrency } from '@/contexts/CurrencyContext';
import { db } from '@/lib/firebase';
import { doc, getDoc, setDoc, updateDoc, collection, query, where, onSnapshot, writeBatch, serverTimestamp, getDocs, addDoc, deleteField } from 'firebase/firestore';
import { useOfflineCountdown } from '@/hooks/useOfflineCountdown';
import { buildArtistPublicUrl, normalizeSlug } from '@/lib/urlHelpers';
import { useMobile } from '@/features/shared/contexts/MobileContext';
import { useArtistLiveStatus } from '@/features/artist-mode/contexts/ArtistLiveStatusContext';
import MobileLiveDashboard from '@/features/shared/components/MobileLiveDashboard';
import MobileAnimatedModal from '@/features/shared/components/MobileAnimatedModal';
import AddSongWizard from '@/features/shared/components/AddSongWizard';
import { saveSessionOnEnd } from '@/lib/sessionHistory';
import { updateReferralCompletion, recordReferralEarning } from '@/lib/referralSystem';
import LiveStatusControls from '@/components/LiveStatusControls';
import AddSongsRequiredModal from '@/components/AddSongsRequiredModal';
import UserNotificationCenter from '@/features/shared/components/UserNotificationCenter';
import { debugLog } from '@/components/DebugConsole';
import { capitalizeWords } from '@/lib/utils';
import { isAddSongWizardCompleted, persistAddSongWizardCompleted, ensureWizardDoneIfHasSongs } from '@/lib/addSongWizardState';
import { buildArtistPublicQrPngBlob, triggerPngBlobDownload } from '@/lib/artistQrDownload';
import { formatMoney, DEFAULT_CURRENCY } from '@/lib/currency';
import { ysr2Mobile } from '@/features/shared/styles/mobileDesignSystem';
import { sanitizeSessionDurationDraft, clampSessionDurationMinutes } from '@/lib/liveSessionDuration';
import { recordArtistAnalyticsEvent, ARTIST_ANALYTICS_EVENT_TYPES } from '@/lib/recordArtistAnalyticsEvent';

const LiveDashboardPage = () => {
    // ✅ USEAUTH NOW PULLS FROM FIREBASE
    const { user, profile, loading: authLoading } = useAuth();
    const currencyContext = useCurrency();
    const payoutCcy = profile?.currency || DEFAULT_CURRENCY;
    const format = currencyContext?.format || ((amount) => formatMoney(amount ?? 0, payoutCcy));
    const { toast } = useToast();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const publicUrl = useMemo(
        () => (profile?.url_slug ? buildArtistPublicUrl(profile.url_slug) : null),
        [profile?.url_slug]
    );
    const mobile = useMobile();
    const { isLive, sessionEndTime, setSessionEndTime, goLive, goOffline } = useArtistLiveStatus();

    // ✅ ALL STATE DECLARATIONS MUST BE BEFORE ANY CONDITIONAL RETURNS
    // (the authLoading guard lives below, after every hook has run — returning
    // early from here changes the hook count between renders and crashes React)
    const [requests, setRequests] = useState([]);
    const [tips, setTips] = useState([]);
    const [songs, setSongs] = useState([]);
    const [playlists, setPlaylists] = useState([]);
    const [setlists, setSetlists] = useState([]);
    const [activePlaylist, setActivePlaylist] = useState(null);
    const [activePlaylistId, setActivePlaylistId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [sessionId, setSessionId] = useState(null);
    const [sessionStartTime, setSessionStartTime] = useState(null);
    const [timeLeft, setTimeLeft] = useState('');
    const [requestCap, setRequestCap] = useState(5);
    const [showStartModal, setShowStartModal] = useState(false);
    /** String so users can clear the field; normalized on Go Live. */
    const [startDurationDraft, setStartDurationDraft] = useState('60');
    const [showEditTimerModal, setShowEditTimerModal] = useState(false);
    const [editTimerMinutes, setEditTimerMinutes] = useState(60);
    const [showGoLiveMessage, setShowGoLiveMessage] = useState(true);
    const [showShareSection, setShowShareSection] = useState(!mobile.isMobile); // Collapsed by default on mobile
    const [qrDownloadBusy, setQrDownloadBusy] = useState(false);
    const [showStatsSection, setShowStatsSection] = useState(!mobile.isMobile); // Collapsed by default on mobile
    const [showSongSearchModal, setShowSongSearchModal] = useState(false);
    const [songSearchQuery, setSongSearchQuery] = useState('');
    const [filteredSongs, setFilteredSongs] = useState([]);
    const [showSessionComplete, setShowSessionComplete] = useState(false);
    const [sessionCompleteData, setSessionCompleteData] = useState(null);
    const [showSongWizard, setShowSongWizard] = useState(false);
    const [showAddSongsModal, setShowAddSongsModal] = useState(false);
    
    // Trial Run Mode State
    const [isTrialMode, setIsTrialMode] = useState(false);
    const [showTrialDemoModal, setShowTrialDemoModal] = useState(false);
    const [trialStep, setTrialStep] = useState(0);
    const [trialRequests, setTrialRequests] = useState([]);
    const [trialInstructionText, setTrialInstructionText] = useState('');
    const [isTypingInstruction, setIsTypingInstruction] = useState(false);
    const [hasGoneLiveBefore, setHasGoneLiveBefore] = useState(null);
    const [daysSinceLastLive, setDaysSinceLastLive] = useState(null);
    const [showTrialPrompt, setShowTrialPrompt] = useState(false);
    const [showTrialPillButton, setShowTrialPillButton] = useState(false);
    const [trialPillTextIndex, setTrialPillTextIndex] = useState(0);
    const [highlightMarkAsPlayedSongId, setHighlightMarkAsPlayedSongId] = useState(null); // Track which song's button should glow
    const [showTrialTipPopup, setShowTrialTipPopup] = useState(false);
    const [trialTipData, setTrialTipData] = useState(null);
    const [goLivePillPulse, setGoLivePillPulse] = useState(false); // Track if Go Live pill should pulse
    const [highlightRequestCount, setHighlightRequestCount] = useState(null); // Track which song's request count should be highlighted
    const [acceptingNewRequests, setAcceptingNewRequests] = useState(true); // Track if artist is accepting new requests
    const [requestsPausedAt, setRequestsPausedAt] = useState(null); // Timestamp when requests were paused
    
    const handleSongWizardComplete = async () => {
        setShowSongWizard(false);
        await persistAddSongWizardCompleted(db, user.uid);
        setTimeout(() => {
            navigate('/admin');
        }, 500);
    };
    
    // Tour wizard state
    const [showTour, setShowTour] = useState(false);
    const [tourStep, setTourStep] = useState(0);
    const [tourData, setTourData] = useState({
        isLive: false,
        showShareSection: true,
        mockRequests: [],
        mockTips: [],
        sessionStartTime: null,
        sessionEndTime: null,
        timeLeft: '45:00'
    });

    // Tour element refs for dynamic positioning
    const goLiveButtonRef = useRef(null);
    const shareSectionRef = useRef(null);
    const incomingRequestsRef = useRef(null);
    const markAsPlayedButtonRef = useRef(null);
    const manualSearchRef = useRef(null);
    const setlistsSectionRef = useRef(null);
    const goOfflineButtonRef = useRef(null);
    
    // Tour spotlight positioning state
    const [spotlightPosition, setSpotlightPosition] = useState({ top: 0, left: 0, width: 0, height: 0 });
    const [instructionPosition, setInstructionPosition] = useState({ top: 0, left: 0 });
    
    /** Synced every render — live_status listener effect only re-runs on user.uid, so closure needs refs. */
    const sessionIdRef = useRef(null);
    const lastFirestoreLiveRef = useRef(false);
    const liveSessionMetaRef = useRef({ sessionId: null, startTime: null, venue: '' });

    // Use the offline countdown hook
    const {
        showOfflineCountdown,
        offlineCountdown,
        isInCountdownMode,
        startOfflineCountdown,
        cancelOfflineCountdown,
        endLiveSession
    } = useOfflineCountdown();

    useEffect(() => {
        sessionIdRef.current = sessionId;
    }, [sessionId]);

    // ✅ VALIDATION: Prevent access if Profile URL is not set up
    useEffect(() => {
        // Only validate after profile has been loaded (not null) and user is authenticated
        if (user && profile !== null && !profile.url_slug) {
            toast({
                title: "Profile URL Required",
                description: "You need to set up your Profile URL before accessing the Live Dashboard.",
                variant: "destructive"
            });
            navigate('/admin/profile');
            return;
        }
    }, [user, profile, navigate, toast]);

    // ✅ TOUR DETECTION: Check if tour parameter is present and start tour
    useEffect(() => {
        const tourParam = searchParams.get('tour');
        if (tourParam === 'true' && !showTour) {
            setShowTour(true);
            setTourStep(0);
            setTourData({
                isLive: false,
                showShareSection: true,
                mockRequests: [],
                mockTips: [],
                sessionStartTime: null,
                sessionEndTime: null,
                timeLeft: '45:00'
            });
            // Clear the tour parameter from URL
            navigate('/admin/live-dashboard', { replace: true });
        }
    }, [searchParams, showTour, navigate]);

    // ✅ ONBOARDING TRIGGER: Check for onboarding trigger from signup
    useEffect(() => {
        const triggerOnboarding = localStorage.getItem('ysr_trigger_onboarding') === 'true';
        console.log('🔍 LiveDashboardPage: Checking onboarding trigger', { 
            triggerOnboarding, 
            showTour, 
            user: !!user,
            triggerFlag: localStorage.getItem('ysr_trigger_onboarding')
        });
        
        if (triggerOnboarding && !showTour && user) {
            console.log('🎯 LiveDashboardPage: Triggering onboarding tour!');
            // Clear the trigger flag
            localStorage.removeItem('ysr_trigger_onboarding');
            // Mark onboarding as completed (since we're showing the tour)
            localStorage.setItem('ysr_onboarding_completed', 'true');
            
            // Start the tour
            setShowTour(true);
            setTourStep(0);
            setTourData({
                isLive: false,
                showShareSection: true,
                mockRequests: [],
                mockTips: [],
                sessionStartTime: null,
                sessionEndTime: null,
                timeLeft: '45:00'
            });
            console.log('🎯 LiveDashboardPage: Tour started!');
        }
    }, [user, showTour]);

    // ✅ SONG WIZARD: Only from explicit trigger; never again after completion or if catalogue exists
    useEffect(() => {
        const showSongWizardTrigger = localStorage.getItem('ysr_show_song_wizard') === 'true';
        const wizardDone = isAddSongWizardCompleted(profile, songs.length);

        if (wizardDone) {
            if (showSongWizardTrigger) localStorage.removeItem('ysr_show_song_wizard');
            if (showSongWizard) setShowSongWizard(false);
            return;
        }

        if (user && !showSongWizard && showSongWizardTrigger) {
            localStorage.removeItem('ysr_show_song_wizard');
            setShowSongWizard(true);
        }
    }, [user, showSongWizard, songs.length, profile?.add_song_wizard_completed]);

    // ✅ START MODAL DETECTION: Check if start parameter is present and show start modal
    useEffect(() => {
        const startParam = searchParams.get('start');
        const durationParam = searchParams.get('duration');
        const showStartModalParam = searchParams.get('showStartModal');
        
        if (showStartModalParam === 'true') {
            console.log('🔍 DEBUG: showStartModal parameter detected, showing session duration modal');
            setShowStartModal(true);
            // Clean up the URL parameter
            const newUrl = new URL(window.location);
            newUrl.searchParams.delete('showStartModal');
            window.history.replaceState({}, '', newUrl);
        } else if (startParam === 'true') {
            console.log('🔍 DEBUG: Start parameter detected, duration:', durationParam);
            if (durationParam) {
                // If duration is provided, go live directly with that duration
                const duration = Math.max(5, Math.min(240, parseInt(durationParam) || 60));
                console.log('🔍 DEBUG: Going live with duration:', duration);
                goLive(duration);
            } else {
                // Show the start modal for duration selection
                console.log('🔍 DEBUG: Showing start modal');
                setShowStartModal(true);
            }
            // Clear the parameters from URL
            navigate('/admin/live-dashboard', { replace: true });
        }
    }, [searchParams, navigate]);

    useEffect(() => {
        if (showStartModal) setStartDurationDraft('60');
    }, [showStartModal]);

    // ✅ OFFLINE DETECTION: Check if offline parameter is present and trigger offline countdown
    useEffect(() => {
        const offlineParam = searchParams.get('offline');
        if (offlineParam === 'true' && isLive) {
            handleStartOfflineCountdown();
            // Clear the offline parameter from URL
            navigate('/admin/live-dashboard', { replace: true });
        }
    }, [searchParams, isLive, navigate]);

    // ✅ TRIAL MODE DETECTION: Show trial demo modal when artist goes live for the first time
    useEffect(() => {
        const checkTrialEligibility = async () => {
            if (!user || !profile) return;
            
            // Check if user dismissed the trial demo permanently (per artist)
            const dismissedPermanently = localStorage.getItem(`trial_demo_dismissed_permanent_${user.uid}`) === 'true';
            if (dismissedPermanently && user.email !== 'trial@trial.com') {
                setShowTrialPrompt(false);
                setShowTrialPillButton(false);
                setShowTrialDemoModal(false);
                return; // Don't show for users who dismissed it (except trial@trial.com)
            }
            
            // Check if demo was completed (per artist)
            const demoCompleted = localStorage.getItem(`trial_demo_completed_${user.uid}`) === 'true';
            if (demoCompleted && user.email !== 'trial@trial.com') {
                setShowTrialPrompt(false);
                setShowTrialPillButton(false);
                setShowTrialDemoModal(false);
                return; // Don't show if demo was completed (except trial@trial.com)
            }
            
            // Check if user has gone live before and when
            let hasGoneLive = false;
            let lastLiveDate = null;
            try {
                const sessionHistoryQuery = query(
                    collection(db, 'session_history'),
                    where('user_id', '==', user.uid),
                    // Order by start_time descending to get most recent
                );
                const sessionHistorySnapshot = await getDocs(sessionHistoryQuery);
                if (sessionHistorySnapshot.docs.length > 0) {
                    hasGoneLive = true;
                    // Get the most recent session
                    const sessions = sessionHistorySnapshot.docs.map(doc => ({
                        id: doc.id,
                        ...doc.data()
                    }));
                    // Sort by start_time to find most recent
                    sessions.sort((a, b) => {
                        const dateA = a.start_time?.toDate?.() || new Date(a.start_time || 0);
                        const dateB = b.start_time?.toDate?.() || new Date(b.start_time || 0);
                        return dateB - dateA;
                    });
                    if (sessions[0]?.start_time) {
                        lastLiveDate = sessions[0].start_time.toDate ? sessions[0].start_time.toDate() : new Date(sessions[0].start_time);
                    }
                }
            } catch (error) {
                console.error('Error checking session history:', error);
            }
            
            setHasGoneLiveBefore(hasGoneLive);
            
            // Check if last live session was more than 7 days ago
            const daysSinceLastLive = lastLiveDate ? Math.floor((new Date() - lastLiveDate) / (1000 * 60 * 60 * 24)) : null;
            setDaysSinceLastLive(daysSinceLastLive);
            const needsRefresher = hasGoneLive && daysSinceLastLive !== null && daysSinceLastLive > 7;
            
            // Show pulsing pill button (NOT modal) when artist has 5+ songs and hasn't gone live before
            // Only show if: not in trial mode, not currently live, hasn't gone live before, has 5+ songs, not dismissed
            // ALWAYS show for trial@trial.com regardless of dismissal/completion status
            const isTrialAccount = user.email === 'trial@trial.com';
            if (!isTrialMode && !isLive && songs.length >= 5) {
                if (isTrialAccount || (!hasGoneLive && !dismissedPermanently && !demoCompleted)) {
                    setShowTrialPillButton(true);
                } else {
                    setShowTrialPillButton(false);
                }
            } else {
                setShowTrialPillButton(false);
            }
            
            // Show trial prompt in dashboard if not dismissed permanently (when offline)
            if (!dismissedPermanently && !demoCompleted && songs.length >= 3 && !isLive && !isTrialMode) {
                setShowTrialPrompt(true);
            }
        };
        
        checkTrialEligibility();
    }, [user, profile, songs.length, isTrialMode, isLive]);
    
    // ✅ ANIMATE TRIAL PILL BUTTON TEXT: Pulse between two texts
    useEffect(() => {
        if (!showTrialPillButton) return;
        
        const interval = setInterval(() => {
            setTrialPillTextIndex(prev => (prev === 0 ? 1 : 0));
        }, 2000); // Switch every 2 seconds
        
        return () => clearInterval(interval);
    }, [showTrialPillButton]);

    // ✅ LOADING STATE: Show loading while profile is being validated
    if (!user || profile === null) {
        return (
            <div className="min-h-screen bg-black flex items-center justify-center">
                <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
            </div>
        );
    }
    
    // Clear any existing session ended data on component mount to prevent showing summary on initial signup
    // But only if we haven't just processed a session
    useEffect(() => {
        const lastProcessedSession = localStorage.getItem('ysr_last_session_processed');
        if (!lastProcessedSession) {
            localStorage.removeItem('sessionEnded');
        }
    }, []);

    // ✅ REFACTORED: Unified initial data fetching from Firestore
    // Load dismissed state from localStorage
    useEffect(() => {
        const dismissed = localStorage.getItem('goLiveMessageDismissed');
        if (dismissed === 'true') {
            setShowGoLiveMessage(false);
        }
        
        // Load share section state from localStorage
        const shareSectionCollapsed = localStorage.getItem('shareSectionCollapsed');
        if (shareSectionCollapsed === 'true') {
            setShowShareSection(false);
        }
    }, []);

    useEffect(() => {
        if (!user) return;
        setLoading(true);

        const fetchData = async () => {
            try {
                // Fetch Songs - simplified query to avoid composite index requirement
                const songsQuery = query(collection(db, 'songs'), where('user_id', '==', user.uid));
            const songsSnapshot = await getDocs(songsQuery);
                const songsData = songsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                
                // Filter in memory instead of requiring a composite index
                const activeSongs = songsData.filter(song => !song.archived);
                setSongs(activeSongs);
            
            // Fetch Playlists
            const playlistsQuery = query(collection(db, 'playlists'), where('user_id', '==', user.uid));
            const playlistsSnapshot = await getDocs(playlistsQuery);
            setPlaylists(playlistsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
            
            // Fetch Setlists (stored in playlists collection)
            const setlistsQuery = query(collection(db, 'playlists'), where('user_id', '==', user.uid));
            const setlistsSnapshot = await getDocs(setlistsQuery);
            setSetlists(setlistsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
            } catch (error) {
                console.error("Error fetching initial data:", error);
                // Don't show error toast for permissions issues during initial load
                if (!error.message.includes('Missing or insufficient permissions')) {
                    toast({ title: "Error", description: "Failed to load some data.", variant: "destructive" });
                }
            } finally {
                // Always set loading to false after fetchData completes
                setLoading(false);
            }
        };

        // Set up listener for the live_status document
        const liveStatusUnsubscribe = onSnapshot(doc(db, "live_status", user.uid), (docSnap) => {
            if (docSnap.exists()) {
                const statusData = docSnap.data();
                const live = statusData.is_live === true;
                const prevFirestoreLive = lastFirestoreLiveRef.current;

                console.log('Live status update - prevFirestoreLive:', prevFirestoreLive, 'isLive:', live, 'sessionId:', statusData.session_id);

                if (live && statusData.session_id) {
                    liveSessionMetaRef.current = {
                        sessionId: statusData.session_id,
                        startTime: statusData.session_start_time?.toDate?.() || null,
                        venue: statusData.venue || '',
                    };
                }

                if (prevFirestoreLive && !live) {
                    if (statusData.force_ended === true) {
                        console.log('Session ended by admin console (history already saved); skipping client duplicate write');
                    } else {
                    const meta = liveSessionMetaRef.current;
                    const sessionIdToUse = statusData.session_id || meta.sessionId || sessionIdRef.current;
                    let startTime =
                        statusData.session_start_time?.toDate?.() ||
                        (meta.startTime && (meta.startTime.toDate ? meta.startTime.toDate() : meta.startTime)) ||
                        null;
                    if (!startTime) {
                        try {
                            const ls = localStorage.getItem('ysr_current_session_start');
                            if (ls) startTime = new Date(ls);
                        } catch (_) {
                            /* ignore */
                        }
                    }
                    const endTime = statusData.session_end_time?.toDate?.() || new Date();
                    const venue = statusData.venue || meta.venue || '';

                    if (sessionIdToUse && startTime) {
                        console.log('Session ended — persisting aggregated session history', {
                            sessionIdToUse,
                            startTime,
                            endTime,
                        });
                        (async () => {
                            try {
                                await saveSessionOnEnd(user.uid, sessionIdToUse, startTime, endTime, venue);
                                console.log('✅ Session history saved (includes funnel metrics)');
                            } catch (error) {
                                console.error('❌ CRITICAL: Failed to save session history:', error);
                            }
                        })();
                        localStorage.setItem(
                            'sessionEnded',
                            JSON.stringify({
                                sessionId: sessionIdToUse,
                                startTime: startTime?.toISOString?.() || null,
                                endTime: endTime?.toISOString?.() || new Date().toISOString(),
                            })
                        );
                    } else {
                        console.warn('⚠️ Session end: missing session id or start time', { sessionIdToUse, startTime });
                    }
                    }
                }

                lastFirestoreLiveRef.current = live;
                
                // Update the ref with the new state AFTER session complete logic
                // (This will be done at the end of this effect)
                
                setSessionId(statusData.session_id || null);
                setRequestCap(statusData.request_cap || 5);
                setSessionEndTime(statusData.session_end_time?.toDate() || null);
                setSessionStartTime(statusData.session_start_time?.toDate() || null);
                
                // Track accepting_new_requests status
                setAcceptingNewRequests(statusData.accepting_new_requests !== false); // Default to true if not set
                setRequestsPausedAt(statusData.requests_paused_at?.toDate() || null);
                
                // Store the active playlist ID, we'll resolve it in a separate effect
                setActivePlaylistId(statusData.active_playlist_id || null);
                
                // Clear offline countdown completion flag when going live
                if (!prevFirestoreLive && live) {
                    localStorage.removeItem('offlineCountdownCompleted');
                    // Only clear session ended data if we're not in the middle of processing a session complete
                    const lastProcessedSession = localStorage.getItem('ysr_last_session_processed');
                    if (!lastProcessedSession) {
                        localStorage.removeItem('sessionEnded');
                    }
                }
                
                // REMOVED: Offline toast logic - this is now handled entirely by the useOfflineCountdown hook
                // The hook manages its own toast display and localStorage state
            } else {
                setActivePlaylistId(null);
            }
            setLoading(false);
        }, (error) => {
            console.error("Error listening to live status:", error);
            // Handle permissions error gracefully
            if (error.code === 'permission-denied' || error.message.includes('Missing or insufficient permissions')) {
                console.warn('Live status permissions issue - user may not have access to live features');
            }
            setLoading(false);
        });

        fetchData();
        return () => liveStatusUnsubscribe();
    }, [user?.uid]); // Only depend on user.uid, not playlists
    
    // Track previous live state for session transition detection
    const [previousIsLive, setPreviousIsLive] = useState(isLive);
    
    useEffect(() => {
        console.log('🔍 DEBUG: Session transition effect - isLive:', isLive, 'previousIsLive:', previousIsLive);

        // If we just went from live to offline, trigger session complete
        if (previousIsLive && !isLive) {
            console.log('🔍 DEBUG: Session transition detected - was live, now offline');
            
            // Get the session data from localStorage if available
            const sessionEndedData = localStorage.getItem('sessionEnded');
            console.log('🔍 DEBUG: Raw sessionEnded data from localStorage:', sessionEndedData);
            if (sessionEndedData) {
                try {
                    const sessionInfo = JSON.parse(sessionEndedData);
                    console.log('🔍 DEBUG: Parsed session info:', sessionInfo);
                    console.log('🔍 DEBUG: Processing session complete for session:', sessionInfo.sessionId);
                    console.log('🔍 DEBUG: Current session ID from state:', sessionId);
                    
                    // Fetch session data and show modal
                    const fetchSessionData = async () => {
                        try {
                            // Use the same approach as Payout Summaries - get all requests and filter in memory
                            console.log('🔍 DEBUG: Fetching all requests for user to filter by session_id');
                            const allRequestsSnapshot = await getDocs(query(
                                collection(db, 'requests'),
                                where('artist_id', '==', user.uid)
                            ));
                            
                            const allRequests = allRequestsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                            console.log('🔍 DEBUG: All requests fetched:', allRequests.length);
                            console.log('🔍 DEBUG: Sample request data:', allRequests[0]);
                            
                            // Filter by session_id in memory (same as Payout Summaries)
                            // Also check for session IDs that are close in time (within 1 second) to handle timing differences
                            const sessionRequests = allRequests.filter(req => {
                                if (req.session_id === sessionInfo.sessionId) {
                                    return true;
                                }
                                
                                // Check if session IDs are close in time (handle timing differences)
                                const currentSessionTime = parseInt(sessionInfo.sessionId.replace('sess-', ''));
                                const requestSessionTime = parseInt(req.session_id.replace('session_', ''));
                                const timeDiff = Math.abs(currentSessionTime - requestSessionTime);
                                
                                // If within 1 second (1000ms), consider it a match
                                if (timeDiff <= 1000) {
                                    console.log('🔍 DEBUG: Found timing-based session match:', {
                                        current: sessionInfo.sessionId,
                                        request: req.session_id,
                                        timeDiff: timeDiff
                                    });
                                    return true;
                                }
                                
                                return false;
                            });
                            console.log('🔍 DEBUG: Filtered session requests:', sessionRequests.length);
                            
                            // Also check for requests without session_id (legacy requests)
                            const legacyRequests = allRequests.filter(req => !req.session_id);
                            console.log('🔍 DEBUG: Legacy requests (no session_id):', legacyRequests.length);
                            
                            // For now, let's also check if there are any requests with different session_id formats
                            const allSessionIds = [...new Set(allRequests.map(req => req.session_id).filter(Boolean))];
                            console.log('🔍 DEBUG: All session IDs found in requests:', allSessionIds);
                            
                            // Check if there are any requests that might belong to this session by time
                            const sessionEndTime = sessionInfo.endTime ? new Date(sessionInfo.endTime) : new Date();
                            const sessionStartTime = sessionInfo.startTime ? new Date(sessionInfo.startTime) : new Date(sessionEndTime.getTime() - 60 * 60 * 1000); // Default 1 hour window
                            
                            const timeBasedRequests = allRequests.filter(req => {
                                const reqTime = req.created_at?.toDate?.() || new Date(req.created_at);
                                return reqTime >= sessionStartTime && reqTime <= sessionEndTime;
                            });
                            console.log('🔍 DEBUG: Time-based requests for session window:', timeBasedRequests.length);
                            
                            // CRITICAL: Only use exact session_id matches for financial accuracy
                            // Do NOT fall back to time-based requests as this could include data from multiple sessions
                            const finalRequests = sessionRequests;
                            
                            if (sessionRequests.length === 0) {
                                console.log('🔍 DEBUG: WARNING - No exact session_id matches found. This session may have no requests or session_id mismatch.');
                                console.log('🔍 DEBUG: Expected session_id:', sessionInfo.sessionId);
                                console.log('🔍 DEBUG: Available session_ids:', allSessionIds);
                            }
                            
                            // For tips, let's check if they exist in a separate collection or are part of requests
                            const sessionTips = finalRequests.filter(req => req.is_tip_only || (req.tip && req.tip > 0));
                            
                            console.log('🔍 DEBUG: Fetched session data:', {
                                sessionId: sessionInfo.sessionId,
                                requestsCount: finalRequests.length,
                                tipsCount: sessionTips.length,
                                requests: finalRequests,
                                tips: sessionTips,
                                allSessionIds: allSessionIds,
                                timeBasedRequests: timeBasedRequests.length,
                                exactMatches: sessionRequests.length,
                                legacyRequests: legacyRequests.length
                            });
                            
                            const sessionMetrics = calculateSessionMetrics(
                                sessionInfo.sessionId,
                                sessionInfo.startTime ? new Date(sessionInfo.startTime) : null,
                                sessionInfo.endTime ? new Date(sessionInfo.endTime) : new Date(),
                                finalRequests,
                                sessionTips
                            );
                            
                            console.log('🔍 DEBUG: Session metrics calculated:', sessionMetrics);
                            
                            // Show the session complete modal
                            const completeData = {
                                sessionId: sessionInfo.sessionId,
                                sessionStartTime: sessionInfo.startTime,
                                sessionEndTime: sessionInfo.endTime,
                                ...sessionMetrics
                            };
                            console.log('🔍 DEBUG: Setting session complete data:', completeData);
                            setSessionCompleteData(completeData);
                            console.log('🔍 DEBUG: Setting showSessionComplete to true');
                            setShowSessionComplete(true);
                            
                            // Mark this session as processed
                            localStorage.setItem('ysr_last_session_processed', sessionInfo.sessionId);
                            
                            // Clear the session ended data
                            localStorage.removeItem('sessionEnded');
                            
                        } catch (error) {
                            console.error('Error fetching session data:', error);
                        }
                    };
                    
                    fetchSessionData();
                    
                } catch (error) {
                    console.error('Error parsing session ended data:', error);
                }
            } else {
                console.log('🔍 DEBUG: No session ended data found in localStorage');
            }
        } else {
            console.log('🔍 DEBUG: Session transition conditions NOT met - previousIsLive:', previousIsLive, 'isLive:', isLive);
        }
        
        // Update the previous state for next comparison
        setPreviousIsLive(isLive);
    }, [isLive, user?.uid, previousIsLive]);

    // ✅ Separate effect to resolve active playlist when playlists or activePlaylistId changes
    useEffect(() => {
        if (activePlaylistId && playlists.length > 0) {
            const activePl = playlists.find(p => p.id === activePlaylistId);
            setActivePlaylist(activePl || null);
        } else {
            setActivePlaylist(null);
        }
    }, [activePlaylistId, playlists]);

    // ✅ REFACTORED: Real-time listener for requests based on the current session
    useEffect(() => {
        if (!user?.uid || !isLive || !sessionId) {
            setRequests([]); // Clear requests if not live or no session
            setTips([]); // Clear tips if not live or no session
            return;
        }

        const requestsQuery = query(
            collection(db, 'requests'),
            where('artist_id', '==', user.uid),
            where('session_id', '==', sessionId)
        );

        const requestsUnsubscribe = onSnapshot(requestsQuery, (snapshot) => {
            const requestsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            console.log("🔄 Requests updated from Firestore:", requestsData.length, "requests");
            console.log("🔄 Request statuses:", requestsData.map(r => ({ id: r.id, status: r.status, song_title: r.song_title })));
            setRequests(requestsData);
        }, (error) => {
            console.error('Error in requests listener:', error);
            // Don't show error for permission issues during initial setup
            if (!error.message?.includes('Missing or insufficient permissions')) {
                console.error('Unexpected error in requests listener:', error);
            }
        });

        // ✅ NEW: Real-time listener for tips received during the current session
        const tipsQuery = query(
            collection(db, 'tips'),
            where('artist_id', '==', user.uid)
        );

        const tipsUnsubscribe = onSnapshot(tipsQuery, (snapshot) => {
            const tipsData = snapshot.docs
                .map(doc => ({ id: doc.id, ...doc.data() }))
                .filter(tip => {
                    // Only include tips received during the current live session
                    return tip.session_id === sessionId;
                });
            setTips(tipsData);
        }, (error) => {
            console.error('Error in tips listener:', error);
            // Don't show error for permission issues during initial setup
            if (!error.message?.includes('Missing or insufficient permissions')) {
                console.error('Unexpected error in tips listener:', error);
            }
        });

        // ✅ NEW: Real-time listener for songs
        const songsQuery = query(
            collection(db, 'songs'),
            where('user_id', '==', user.uid)
        );

        const songsUnsubscribe = onSnapshot(songsQuery, (snapshot) => {
            const songsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            const activeSongs = songsData.filter(song => !song.archived);
            console.log("🔄 Songs updated from Firestore:", activeSongs.length, "songs");
            setSongs(activeSongs);
            void ensureWizardDoneIfHasSongs(db, user.uid, activeSongs.length);
        }, (error) => {
            console.error('Error in songs listener:', error);
            // Don't show error for permission issues during initial setup
            if (!error.message?.includes('Missing or insufficient permissions')) {
                console.error('Unexpected error in songs listener:', error);
            }
        });

        return () => {
            requestsUnsubscribe();
            tipsUnsubscribe();
            songsUnsubscribe();
        };
    }, [user?.uid, isLive, sessionId, sessionStartTime]);


    // ✅ REFACTORED: Going live writes to Firestore
    const startLiveSession = async (durationMinutes) => {
        console.log('🔍 DEBUG: goLive function called with duration:', durationMinutes);
        console.log('🔍 DEBUG: user:', user?.uid);
        console.log('🔍 DEBUG: loading:', loading);
        
        if (!user) {
            console.log('🔍 DEBUG: No user, returning');
            return;
        }
        
        // Don't show error if data is still loading, but allow going live if user is authenticated
        if (loading && !user) {
            console.log('🔍 DEBUG: Still loading and no user, showing toast');
            toast({ 
                title: "Loading...", 
                description: "Please wait while we load your data.", 
                variant: "default" 
            });
            return;
        }
        
        if (loading) {
            console.log('🔍 DEBUG: Still loading but user is authenticated, proceeding with goLive');
        }
        
        // CRITICAL: Check if user has songs in the database - NO EXCEPTIONS!
        console.log('🔍 DEBUG: Checking songs array:', songs);
        console.log('🔍 DEBUG: Songs array length:', songs?.length);
        
        if (!Array.isArray(songs) || songs.length === 0) {
            console.log('🚫 CRITICAL: No songs found in database, blocking goLive');
            setShowAddSongsModal(true);
            return;
        }
        
        console.log('✅ Songs check passed, proceeding with goLive');
        
        // Request notification permission when going live (natural moment for user)
        console.log('🔍 DEBUG: Checking notification permission');
        if (typeof window !== 'undefined' && 'Notification' in window) {
            console.log('🔍 DEBUG: Notification API available');
            if (Notification.permission === 'default') {
                console.log('🔍 DEBUG: Requesting notification permission');
                try {
                    const permission = await Notification.requestPermission();
                    console.log('🔍 DEBUG: Notification permission result:', permission);
                    if (permission === 'granted') {
                        // Push notifications removed - service workers disabled
                        toast({
                            title: "Notifications Enabled! 🔔",
                            description: "Browser notifications enabled",
                        });
                    } else if (permission === 'denied') {
                        toast({
                            title: "Notifications Disabled",
                            description: "You can enable notifications later in your browser settings or in your Profile",
                        });
                    }
                } catch (error) {
                    console.error('❌ Error requesting notification permission:', error);
                }
            } else {
                console.log('🔍 DEBUG: Notification permission already set:', Notification.permission);
                
                // Push notifications removed - service workers disabled
            }
        } else {
            console.log('🔍 DEBUG: Notification API not available in this environment');
        }
        
        console.log('🔍 DEBUG: Creating session data');
        const newSessionId = `session_${Date.now()}`;
        const startTime = new Date();
        const endTime = new Date(startTime.getTime() + durationMinutes * 60 * 1000);

        const liveStatusData = {
            user_id: user.uid,
            is_live: true,
            session_id: newSessionId,
            session_start_time: startTime,
            session_end_time: endTime,
            request_cap: 5, // Reset to default
            active_playlist_id: null,
            active_song_tags: [],
        };
        
        console.log('🔍 DEBUG: Session data created:', liveStatusData);

        try {
            console.log('🔍 DEBUG: Writing to Firestore with data:', liveStatusData);
            console.log('🔍 DEBUG: User object:', user);
            console.log('🔍 DEBUG: User UID:', user.uid);
            console.log('🔍 DEBUG: User getIdToken available:', typeof user.getIdToken);
            
            // Get the Firebase ID token to ensure authentication
            if (user.getIdToken) {
                try {
                    // Force refresh the token to ensure it's valid
                    const idToken = await user.getIdToken(true);
                    console.log('🔍 DEBUG: Firebase ID token obtained (force refresh):', idToken ? 'Present' : 'Missing');
                    console.log('🔍 DEBUG: ID token preview:', idToken ? idToken.substring(0, 50) + '...' : 'None');
                    
                    if (!idToken) {
                        console.error('❌ No valid ID token available, cannot proceed with going live');
                        toast({ title: "Authentication Error", description: "Please try logging out and logging back in.", variant: "destructive" });
                        return;
                    }
                    
                    // Add a small delay to ensure the token is properly set
                    await new Promise(resolve => setTimeout(resolve, 100));
                } catch (tokenError) {
                    console.error('❌ Error getting ID token:', tokenError);
                    toast({ title: "Authentication Error", description: "Unable to verify authentication. Please try again.", variant: "destructive" });
                    return;
                }
            }
            
            await setDoc(doc(db, "live_status", user.uid), liveStatusData);
            
            // Create a test pending request for trial@trial.com to test pause functionality
            if (user.email === 'trial@trial.com') {
                try {
                    // Check if a test request already exists for this session
                    const testRequestQuery = query(
                        collection(db, 'requests'),
                        where('artist_id', '==', user.uid),
                        where('session_id', '==', newSessionId),
                        where('requester_name', '==', 'Test User')
                    );
                    const existingTestRequests = await getDocs(testRequestQuery);
                    
                    if (existingTestRequests.empty && songs.length > 0) {
                        // Create a test request with the first song
                        const testSong = songs[0];
                        await addDoc(collection(db, 'requests'), {
                            artist_id: user.uid,
                            session_id: newSessionId,
                            song_id: testSong.id,
                            song_title: testSong.title,
                            original_artist: testSong.original_artist || testSong.artist || 'Unknown Artist',
                            requester_name: 'Test User',
                            requester_email: '',
                            note: 'Test request for pause functionality',
                            amount_paid: 5.00,
                            tip: 1.00,
                            is_tip_only: false,
                            status: 'pending',
                            created_at: serverTimestamp()
                        });
                        console.log('✅ Test request created for trial@trial.com');
                    }
                } catch (testRequestError) {
                    console.error('⚠️ Error creating test request:', testRequestError);
                    // Don't fail the go live process if test request creation fails
                }
            }
            
            // Record session start locally for strict summary bounds
            try {
                localStorage.setItem('ysr_current_session_start', startTime.toISOString());
            } catch (_) {}
            console.log('🔍 DEBUG: Successfully wrote to Firestore');
            // Only show toast on desktop
            if (!mobile.isMobile) {
                toast({ title: "You're Live!", description: "Share your page to start receiving requests." });
            }
        } catch (error) {
            console.error("❌ Error going live:", error);
            console.error("❌ Error details:", {
                code: error.code,
                message: error.message,
                stack: error.stack
            });
            toast({ title: "Error", description: "Could not start live session.", variant: "destructive" });
        }
    };

    // ✅ REFACTORED: Going offline updates Firestore
    const endLiveSessionLocal = async () => {
        if (!user) return;

        try {
            console.log('🔍 DEBUG: Ending live session for user:', user.uid);
            console.log('🔍 DEBUG: Current sessionId when ending:', sessionId);
            
            // Update Firestore to set is_live: false but preserve session_id
            await setDoc(doc(db, "live_status", user.uid), {
                is_live: false,
                session_end_time: new Date(),
                session_id: sessionId, // Preserve the session_id
            }, { merge: true });
            
            console.log('🔍 DEBUG: Successfully updated Firestore to offline');
            
            // Show success message
            if (!mobile.isMobile) {
                toast({ title: "Session Ended", description: "Your live session has been ended." });
            }
        } catch (error) {
            console.error("❌ Error ending live session:", error);
            toast({ title: "Error", description: "Could not end live session.", variant: "destructive" });
        }
    };

    const dismissGoLiveMessage = () => {
        setShowGoLiveMessage(false);
        localStorage.setItem('goLiveMessageDismissed', 'true');
    };

    // ✅ TRIAL RUN MODE: Type out instructional text with animation
    const typeInstruction = (text, callback) => {
        setIsTypingInstruction(true);
        setTrialInstructionText('');
        let index = 0;
        const typingInterval = setInterval(() => {
            if (index < text.length) {
                setTrialInstructionText(text.substring(0, index + 1));
                index++;
            } else {
                clearInterval(typingInterval);
                setIsTypingInstruction(false);
                if (callback) {
                    setTimeout(() => {
                        callback();
                    }, 900); // 0.9 seconds delay after typing completes (0.4s + 0.5s extra)
                }
            }
        }, 60); // Slowed down from 30ms to 60ms per character for easier reading
        
        // Store interval ID for cleanup if needed
        return typingInterval;
    };

    // ✅ TRIAL RUN MODE: Start the trial demo
    const startTrialDemo = async () => {
        try {
            console.log('🎬 startTrialDemo called', { user: !!user, songsLength: songs.length });
            
            if (!user || songs.length < 3) {
                toast({
                    title: "Cannot Start Demo",
                    description: "You need at least 3 songs in your catalogue for the demo.",
                    variant: "destructive"
                });
                return;
            }

            console.log('🎬 Closing modal and starting trial');
            setShowTrialDemoModal(false);
            setIsTrialMode(true);
            localStorage.setItem('trial_mode_active', 'true'); // Flag for AdminDashboard to detect
            setTrialStep(0);
            setTrialRequests([]);
            
            // Get first 3 songs from artist's catalogue
            const demoSongs = (songs && Array.isArray(songs) && songs.length >= 3) ? songs.slice(0, 3) : [];
            console.log('🎬 Demo songs:', demoSongs.map(s => ({ title: s.title, artist: s.original_artist || s.artist })));
            
            if (demoSongs.length < 3) {
                toast({
                    title: "Cannot Start Demo",
                    description: "You need at least 3 songs in your catalogue for the demo.",
                    variant: "destructive"
                });
                return;
            }
            
            // Start live session in trial mode (don't write to Firestore)
            const newSessionId = `trial_session_${Date.now()}`;
            setSessionId(newSessionId);
            setSessionStartTime(new Date());
            const endTime = new Date(new Date().getTime() + 60 * 60 * 1000); // 1 hour
            setSessionEndTime(endTime);
            
            // STEP 0: Toggle Go Live pill ON first (before anything else)
            // The pill will automatically show as ON because isTrialMode is true
            // Then pulse it 5 times
            setGoLivePillPulse(true);
            localStorage.setItem('go_live_pill_pulse', 'true'); // Flag for AdminDashboard
            let pulseCount = 0;
            const pulseInterval = setInterval(() => {
                pulseCount++;
                if (pulseCount >= 5) {
                    clearInterval(pulseInterval);
                    setGoLivePillPulse(false); // Stop pulsing after 5 times
                    localStorage.setItem('go_live_pill_pulse', 'false'); // Clear flag
                }
            }, 600); // Pulse every 600ms (5 pulses = 3 seconds total)
            
            // Wait 1 second after toggle/pulse, then start the rest of the demo
            setTimeout(() => {
                // Step 1: Show initial instruction with typing animation
                setTimeout(() => {
                    typeInstruction("You're live! Now all you need are Requests; share your URL and QR to your audience...", () => {
                        // Step 2: Add Song 1 with fade-in animation (0.4 seconds after text completes)
                        // No additional setTimeout - the 0.4s delay is already handled by typeInstruction callback
                        console.log('🎬 Adding Song 1');
                        const song1 = demoSongs[0];
                        const request1 = {
                            id: `trial_req_1_${Date.now()}`,
                            song_id: song1.id,
                            song_title: song1.title,
                            original_artist: song1.original_artist || song1.artist || 'Unknown Artist',
                            artist_id: user.uid,
                            session_id: newSessionId,
                            status: 'pending',
                            amount_paid: song1.price || 5.00,
                            tip: 0,
                            requester_name: 'Demo Fan',
                            requester_email: 'demo@example.com',
                            created_at: new Date(),
                            is_trial: true
                        };
                        console.log('🎬 Setting trial requests:', [request1]);
                        setTrialRequests([request1]);
                        setTrialStep(1);
                        // Wait 0.25 seconds after request fades in, then start next instruction
                        setTimeout(() => {
                            typeInstruction("Amazing! You've got your first request! Let's see if we get another...", () => {
                                // Step 3: Add Song 2
                                setTimeout(() => {
                                    const song2 = demoSongs[1];
                                    const request2 = {
                                        id: `trial_req_2_${Date.now()}`,
                                        song_id: song2.id,
                                        song_title: song2.title,
                                        original_artist: song2.original_artist || song2.artist || 'Unknown Artist',
                                        artist_id: user.uid,
                                        session_id: newSessionId,
                                        status: 'pending',
                                        amount_paid: song2.price || 5.00,
                                        tip: 0,
                                        requester_name: 'Another Fan',
                                        requester_email: 'fan2@example.com',
                                        created_at: new Date(),
                                        is_trial: true
                                    };
                                    setTrialRequests([request1, request2]);
                                    setTrialStep(2);
                                    typeInstruction("Fantastic!! Let's finish up with the song you're performing now, and get ready to fulfill your first YSR request!", () => {
                                        // Step 4: Add Song 1 again (duplicate request)
                                        setTimeout(() => {
                                            const request1Duplicate = {
                                                id: `trial_req_1_dup_${Date.now()}`,
                                                song_id: song1.id,
                                                song_title: song1.title,
                                                original_artist: song1.original_artist || song1.artist || 'Unknown Artist',
                                                artist_id: user.uid,
                                                session_id: newSessionId,
                                                status: 'pending',
                                                amount_paid: song1.price || 5.00,
                                                tip: 0,
                                                requester_name: 'Second Fan',
                                                requester_email: 'fan3@example.com',
                                                created_at: new Date(),
                                                is_trial: true
                                            };
                                            setTrialRequests([request1, request2, request1Duplicate]);
                                            setTrialStep(3);
                                            setHighlightMarkAsPlayedSongId(song1.id); // Highlight the duplicate song's button
                                            setHighlightRequestCount(song1.id); // Highlight the "2" and Users icon
                                            typeInstruction(`Look at that!! Two people have requested the same song! Double bubble! Perform ${song1.title} to your audience then Mark As Played!`, () => {
                                                setTrialStep(4); // Wait for mark as played
                                                // Fade out the instruction modal after a short delay to allow artist to tap button
                                                setTimeout(() => {
                                                    setTrialInstructionText('');
                                                }, 1000); // Fade out after 1 second
                                            });
                                        }, 2500); // Halved from 5000ms to 2500ms for faster pace
                                    });
                                }, 2500); // Halved from 5000ms to 2500ms for faster pace
                            });
                        }, 250); // Wait 0.25 seconds after request fades in
                    });
                }, 1000); // Delay before starting typing animation
            }, 500); // Initial half-second pause before starting
        } catch (error) {
            console.error('❌ Error starting trial demo:', error);
            toast({
                title: "Error Starting Demo",
                description: error.message || "Something went wrong. Please try again.",
                variant: "destructive"
            });
            setIsTrialMode(false);
            setShowTrialDemoModal(true);
        }
    };

    // ✅ TRIAL RUN MODE: Handle mark as played in trial mode
    const handleTrialMarkAsPlayed = async (req) => {
        if (!isTrialMode) return;
        
        // Clear the highlight when marked as played
        setHighlightMarkAsPlayedSongId(null);
        
        // Mark the request(s) as played
        const requestIds = req.all_request_ids || (req.id ? [req.id] : []);
        setTrialRequests(prev => {
            if (!prev || !Array.isArray(prev)) return [];
            return prev.map(r => {
                if (r && requestIds.includes(r.id)) {
                    return { ...r, status: 'played' };
                }
                return r;
            });
        });
        
        // If we're at step 4 (waiting for mark as played), move to tip step
        if (trialStep === 4) {
            setTimeout(() => {
                // Add a tip
                const tip = {
                    id: `trial_tip_${Date.now()}`,
                    artist_id: user.uid,
                    session_id: sessionId,
                    amount: 2.50,
                    requester_name: 'Generous Fan',
                    message: 'Great performance!',
                    created_at: new Date(),
                    is_trial: true
                };
                // Show tip popup
                setTrialTipData(tip);
                setShowTrialTipPopup(true);
                setTrialStep(5);
                // Auto-hide tip popup after 4 seconds
                setTimeout(() => {
                    setShowTrialTipPopup(false);
                }, 4000);
                typeInstruction(`🎉 Congratulations! You received a tip! Generous Fan just tipped you ${format(2.50)}!`, () => {
                    // Wait 0.3 seconds after tip text finishes, then show instruction about completing requests
                    setTimeout(() => {
                        typeInstruction("Great work! Now complete and fulfill all remaining requests before going offline.", () => {
                            // Highlight the last song (Song 3) for marking as played
                            if (songs && Array.isArray(songs) && songs.length >= 3) {
                                const song3 = songs[2];
                                setHighlightMarkAsPlayedSongId(song3.id);
                                setTrialStep(7); // New step: waiting for last song to be marked as played
                            } else {
                                // Fallback: end demo if songs array is invalid
                                endTrialDemo();
                            }
                        });
                    }, 300); // 0.3 seconds wait after tip text finishes
                });
            }, 1000);
        }
        
        // If we're at step 7 (waiting for last song to be marked as played), go offline
        if (trialStep === 7) {
            // Clear the highlight
            setHighlightMarkAsPlayedSongId(null);
            // Wait 0.3 seconds, then visualize going offline
            setTimeout(() => {
                // Mark all remaining songs as played
                setTrialRequests(prev => {
                    if (!prev || !Array.isArray(prev)) return [];
                    return prev.map(r => {
                        if (r && r.status === 'pending') {
                            return { ...r, status: 'played' };
                        }
                        return r;
                    });
                });
                setTrialStep(8); // Step 8: showing offline visualization
                // Visualize going offline (isLive will be false via trialStep !== 8 condition)
                // Show offline timer/state
                typeInstruction("Perfect! All requests fulfilled. You're now offline. Great job! 🎉", () => {
                    setTimeout(() => {
                        endTrialDemo();
                    }, 2000);
                });
            }, 300); // 0.3 seconds wait after marking last song
        }
    };

    // ✅ TRIAL RUN MODE: End trial demo
    const endTrialDemo = async () => {
        setIsTrialMode(false);
        localStorage.removeItem('trial_mode_active'); // Clear flag
        localStorage.removeItem('go_live_pill_pulse'); // Clear pulse flag
        setTrialRequests([]);
        setTrialStep(0);
        setTrialInstructionText('');
        setShowTrialTipPopup(false);
        setTrialTipData(null);
        setGoLivePillPulse(false);
        setHighlightRequestCount(null);
        setSessionId(null);
        setSessionStartTime(null);
        setSessionEndTime(null);
        
        // CRITICAL: Ensure artist is set to offline in Firestore when trial ends
        if (user?.uid) {
            try {
                const liveStatusRef = doc(db, 'live_status', user.uid);
                const currentStatus = await getDoc(liveStatusRef);
                
                // Only update if currently live (don't overwrite if already offline)
                if (currentStatus.exists() && currentStatus.data().is_live) {
                    await updateDoc(liveStatusRef, {
                        is_live: false,
                        session_id: null,
                        session_start_time: null,
                        session_end_time: serverTimestamp()
                    });
                    console.log('✅ Trial ended: Set artist to offline in Firestore');
                }
            } catch (error) {
                console.error('Error setting offline after trial:', error);
                // Try to set offline anyway as fallback
                try {
                    await setDoc(doc(db, 'live_status', user.uid), {
                        is_live: false,
                        session_id: null
                    }, { merge: true });
                } catch (fallbackError) {
                    console.error('Fallback offline setting failed:', fallbackError);
                }
            }
            
            // Mark that demo was completed, so modal won't show again
            localStorage.setItem(`trial_demo_completed_${user.uid}`, 'true');

            await recordArtistAnalyticsEvent(user, {
                eventType: ARTIST_ANALYTICS_EVENT_TYPES.LIVE_TRIAL_DEMO_COMPLETED,
                sourcePath: '/admin/live-dashboard',
            });
            await setDoc(
                doc(db, 'profiles', user.uid),
                { trial_demo_completed_at: serverTimestamp() },
                { merge: true },
            );
        }
        
        toast({
            title: "Demo Complete! 🎉",
            description: "You're now ready to go live for real!",
        });
    };

    const toggleShareSection = () => {
        const newState = !showShareSection;
        setShowShareSection(newState);
        localStorage.setItem('shareSectionCollapsed', (!newState).toString());
    };

    const handleDownloadQrPng = useCallback(async () => {
        if (!publicUrl || qrDownloadBusy) return;
        setQrDownloadBusy(true);
        try {
            const blob = await buildArtistPublicQrPngBlob(publicUrl);
            const safe = normalizeSlug(profile?.url_slug).replace(/[^a-z0-9-_]/g, '-') || 'qr';
            triggerPngBlobDownload(blob, `YourSongRequest-${safe}-qr.png`);
        } catch (e) {
            console.error('download qr png', e);
            toast({
                title: 'Could not build QR',
                description: 'Try again in a moment.',
                variant: 'destructive',
            });
        } finally {
            setQrDownloadBusy(false);
        }
    }, [publicUrl, qrDownloadBusy, profile?.url_slug, toast]);

    const toggleStatsSection = () => {
        const newState = !showStatsSection;
        setShowStatsSection(newState);
        localStorage.setItem('statsSectionCollapsed', (!newState).toString());
    };

    const selectSetlist = async (playlistId) => {
        if (!user) return;
        
        try {
            await updateDoc(doc(db, 'live_status', user.uid), {
                active_playlist_id: playlistId
            });
            if (!mobile.isMobile) {
                toast({ title: "Setlist Updated", description: "Your active setlist has been updated." });
            }
        } catch (error) {
            console.error("Error updating setlist:", error);
            toast({ title: "Error", description: "Failed to update setlist.", variant: "destructive" });
        }
    };

    const clearSetlist = async () => {
        if (!user) return;
        
        try {
            await updateDoc(doc(db, 'live_status', user.uid), {
                active_playlist_id: null
            });
            if (!mobile.isMobile) {
                toast({ title: "Setlist Cleared", description: "All songs are now available for requests." });
            }
        } catch (error) {
            console.error("Error clearing setlist:", error);
            toast({ title: "Error", description: "Failed to clear setlist.", variant: "destructive" });
        }
    };

    const reEnableSong = async (request) => {
        if (!user || !request) return;

        const requestIds = Array.isArray(request.all_request_ids) && request.all_request_ids.length > 0
            ? request.all_request_ids
            : request.id
                ? [request.id]
                : [];

        if (requestIds.length === 0) {
            toast({ title: "Error", description: "Could not find request to undo.", variant: "destructive" });
            return;
        }

        try {
            const batch = writeBatch(db);
            
            // Update all related requests back to 'pending'
            requestIds.forEach(id => {
                const reqRef = doc(db, 'requests', id);
                batch.update(reqRef, {
                    status: 'pending',
                    played_at: deleteField(),
                    updated_at: serverTimestamp(),
                });
            });

            await batch.commit();
            toast({ title: "Song Re-enabled", description: "The song is now available for requests again." });
        } catch (error) {
            console.error("Error re-enabling song:", error);
            toast({ title: "Error", description: "Could not re-enable song.", variant: "destructive" });
        }
    };

    const markSongAsPlayedFromSearch = async (song) => {
        if (!user || !sessionId) return;

        try {
            // Create a new request document for this song
            const requestRef = doc(collection(db, 'requests'));
            await setDoc(requestRef, {
                artist_id: user.uid,
                song_id: song.id,
                song_title: song.title,
                requester_name: 'Artist (Manual)',
                status: 'played',
                session_id: sessionId,
                amount_paid: 0,
                created_at: serverTimestamp(),
                updated_at: serverTimestamp()
            });

            if (!mobile.isMobile) {
                toast({ title: "Song Marked as Played", description: `${song.title} has been marked as played.` });
            }
            setShowSongSearchModal(false);
            setSongSearchQuery('');
        } catch (error) {
            console.error("Error marking song as played:", error);
            toast({ title: "Error", description: "Could not mark song as played.", variant: "destructive" });
        }
    };

    const calculateSessionMetrics = (sessionId, sessionStartTime, sessionEndTime, sessionRequests = [], sessionTips = []) => {
        // Use provided session data or fall back to current requests
        const requestsData = sessionRequests.length > 0 ? sessionRequests : requests.filter(req => req.session_id === sessionId);

        // Derive earnings/tips from requests to handle both schemas (tips in separate collection or as is_tip_only requests)
        let totalSongEarnings = 0; // song price only
        let totalTips = 0;
        let played = 0;
        let pending = 0;
        const songRequestCounts = {};

        for (const req of requestsData) {
            // CRITICAL: Only count requests that went through Stripe payment (have payment_intent_id)
            // "Mark As Played" requests (without payment_intent_id) should NOT be counted as paid
            if (!req.payment_intent_id) {
                continue; // Skip requests without payment_intent_id
            }
            
            const amountPaid = Number(req.amount_paid || 0);
            const tipAmt = Number(req.tip || 0);
            const isTipOnly = !!req.is_tip_only;

            if (isTipOnly) {
                totalTips += amountPaid || tipAmt; // tip-only stored as amount_paid and/or tip
            } else {
                totalSongEarnings += Math.max(0, amountPaid - tipAmt);
                totalTips += tipAmt;
                // Count only non-tip requests for "requests" and most requested song
                const songKey = `${req.song_title} - ${req.original_artist || 'Unknown Artist'}`;
                songRequestCounts[songKey] = (songRequestCounts[songKey] || 0) + 1;
            }

            if (req.status === 'played') played += 1;
            if (req.status === 'pending') pending += 1;
        }

        // Note: sessionTips array is derived from the same requestsData, so tips are already counted above
        // Only add tips from a separate tips collection if it exists and is different from requestsData
        // For now, we're not using a separate tips collection, so this is commented out to prevent double-counting
        /*
        if (Array.isArray(sessionTips) && sessionTips.length > 0) {
            for (const t of sessionTips) {
                totalTips += Number(t.amount || t.tip || 0);
            }
        }
        */

        const mostRequestedSong = Object.entries(songRequestCounts)
            .sort(([, a], [, b]) => b - a)[0] || null;

        return {
            sessionId,
            startTime: sessionStartTime,
            endTime: sessionEndTime,
            totalRequests: Object.values(songRequestCounts).reduce((s, c) => s + c, 0),
            totalEarnings: totalSongEarnings,
            totalTips,
            playedRequests: played,
            pendingRequests: pending,
            mostRequestedSong: mostRequestedSong ? {
                title: mostRequestedSong[0].split(' - ')[0],
                artist: mostRequestedSong[0].split(' - ')[1],
                count: mostRequestedSong[1]
            } : null
        };
    };

    const handleSongSearch = (query) => {
        setSongSearchQuery(query);
        if (!query.trim()) {
            setFilteredSongs([]);
            return;
        }

        const queryLower = query.toLowerCase();
        const filtered = songs.filter(song => {
            const title = (song.title || '').toLowerCase();
            const artist = (song.artist || '').toLowerCase();
            const originalArtist = (song.original_artist || '').toLowerCase();
            return title.includes(queryLower) || artist.includes(queryLower) || originalArtist.includes(queryLower);
        });
        setFilteredSongs(filtered.slice(0, 10)); // Limit to 10 results
    };


    
    // Custom startOfflineCountdown - countdown banner will always be visible
    const handleStartOfflineCountdown = async () => {
        // Call the hook's startOfflineCountdown
        await startOfflineCountdown();
    };
    
    // ✅ REFACTORED: Marking as played uses a Firestore Batch Write
    const markAsPlayed = async (req) => {
        // Handle trial mode separately
        if (isTrialMode) {
            handleTrialMarkAsPlayed(req);
            return;
        }
        
        console.log("🚨🚨🚨 FUNCTION CALLED - FIRST LINE OF markAsPlayed 🚨🚨🚨");
        
        try {
            console.log("🚨 MARK AS PLAYED CALLED - Function entry point (LiveDashboardPage.jsx)");
            console.log("🚨 User:", user?.uid);
            console.log("🚨 Request object:", req);
            
            if (!user) {
                console.error("❌ No user found, cannot mark as played");
                return;
            }
        } catch (error) {
            console.error("❌ Error in markAsPlayed function start:", error);
            return;
        }

        console.log("🎵 Marking as played:", req);
        console.log("🎵 DEBUG: Function started, about to log debug info");
        console.log("🚨 UNIQUE DEBUG: This is from /src/pages/LiveDashboardPage.jsx - if you see this, the function is working!");
        
        // Test if recordReferralEarning function exists
        console.log("🧪 TESTING: recordReferralEarning function:", typeof recordReferralEarning);
        
        debugLog('INFO', 'Marking song request as played', { 
            requestId: req.id || req.all_request_ids?.[0],
            songTitle: req.song_title,
            requestIds: req.all_request_ids || [req.id],
            userId: user.uid
        });
        console.log("🎵 DEBUG: Debug log completed, continuing with function");

        // Handle both individual requests and grouped requests
        let requestIds = [];
        let songTitle = '';
        
        if (req.all_request_ids) {
            // This is a grouped request
            requestIds = req.all_request_ids;
            songTitle = req.song_title;
            console.log("🎵 Grouped request - IDs:", requestIds);
        } else if (req.id) {
            // This is an individual request
            requestIds = [req.id];
            songTitle = req.song_title;
            console.log("🎵 Individual request - ID:", req.id);
        } else {
            console.error("❌ Invalid request object:", req);
            return;
        }

        try {
            console.log("🎵 Starting batch write for request IDs:", requestIds);
            const batch = writeBatch(db);

            // Update all related requests to 'played'
            requestIds.forEach(id => {
                const reqRef = doc(db, 'requests', id);
                console.log("🎵 Updating request:", id, "to status: played");
                batch.update(reqRef, { 
                    status: 'played',
                    played_at: serverTimestamp()
                });
            });

            // ❌ REMOVED: Transaction creation when marking as played
            // Transactions should ONLY be created from Stripe payment events (webhooks)
            // Creating transactions here causes double-counting since balance already comes from Stripe
            // The balance is tracked via Stripe (deferred_payments + Stripe balance), not transactions
            // Transactions are historical records only and should not be used for balance calculation

            console.log("🎵 Committing batch write...");
            await batch.commit();
            console.log("✅ Successfully marked as played:", songTitle);
            console.log("🎵 About to calculate total earnings...");

            // Award competition points for completed song(s) — skip admin demo lines
            try {
              const { awardPointsForSongCompletion, awardPointsForReferral } = await import('@/lib/competition');
              const nonDemoIds = requestIds.filter((id) => !requests.find((x) => x.id === id)?.is_admin_demo);
              if (nonDemoIds.length > 0) {
                for (const requestId of nonDemoIds) {
                  await awardPointsForSongCompletion(user.uid, requestId);
                }
                await awardPointsForReferral(user.uid, nonDemoIds[0]);
                console.log("🎯 Competition points awarded");
              }
            } catch (compError) {
              console.warn('Could not award competition points:', compError);
            }
            
            // Determine related requests for earnings calculations
            const relatedRequests = Array.isArray(req.relatedRequests) && req.relatedRequests.length > 0
              ? req.relatedRequests.filter(r => !r.is_trial && !r.is_admin_demo) // Exclude trial + admin demos
              : requests.filter(requestItem => requestIds.includes(requestItem.id) && !requestItem.is_trial && !requestItem.is_admin_demo); // Exclude trial + admin demos
            
            // Calculate total song earnings from completed requests (excluding tips)
            const totalSongEarnings = relatedRequests.reduce((sum, request) => {
                // CRITICAL: Double-check to exclude trial requests
                if (request.is_trial) return sum;
                const songPrice = (request.amount_paid || 0) - (request.tip || 0);
                return sum + songPrice;
            }, 0);
            console.log("🎵 Total song earnings calculated (excluding tips):", totalSongEarnings);
            console.log("🎵 Related requests:", relatedRequests.map(r => ({ 
                id: r.id, 
                amount_paid: r.amount_paid, 
                tip: r.tip || 0, 
                song_price: (r.amount_paid || 0) - (r.tip || 0) 
            })));
            
            // Record referral earnings for each completed request (10% of song request earnings, not tips)
            console.log("🎵 REFERRAL DEBUG - About to process referral earnings...");
            console.log("🎵 REFERRAL DEBUG - totalSongEarnings:", totalSongEarnings);
            console.log("🎵 REFERRAL DEBUG - relatedRequests:", relatedRequests);
            
            if (totalSongEarnings > 0) {
                console.log("🎵 Recording referral earnings for total song earnings:", totalSongEarnings);
                
                // Force the debugLog to show in floating console
                debugLog('REFERRAL_PROCESS', 'Processing referral earnings', {
                    userId: user.uid,
                    totalSongEarnings,
                    requestCount: relatedRequests.length
                });
                
                try {
                    // Update referral completion status (this handles both individual earnings and bulk update)
                    console.log("🎵 Updating referral completion for user:", user.uid, "song earnings:", totalSongEarnings);
                    await updateReferralCompletion(user.uid, totalSongEarnings);
                    
                    debugLog('REFERRAL_SUCCESS', 'Referral earnings processed successfully', {
                        userId: user.uid,
                        totalSongEarnings
                    });
                } catch (referralError) {
                    console.error('Error updating referral completion:', referralError);
                    debugLog('REFERRAL_ERROR', 'Error processing referral earnings', {
                        error: referralError.message
                    });
                }
            } else {
                console.log("🎵 No earnings to record (totalEarnings = 0)");
                debugLog('REFERRAL_SKIP', 'No earnings to record', {
                    totalSongEarnings
                });
            }
            
            if (!mobile.isMobile) {
                toast({ title: "Song Marked as Played!", description: `${songTitle} has been marked as played.` });
            }
        } catch (error) {
            console.error("❌ Error marking as played:", error);
            console.error("❌ Error details:", error.message);
            console.error("❌ Error code:", error.code);
            toast({ title: "Error", description: "Could not mark as played.", variant: "destructive" });
        }
    };
    
    // Toggle accepting new requests
    const handleToggleAcceptingRequests = async () => {
        if (!user || !isLive || isTrialMode) return;
        
        try {
            const liveStatusRef = doc(db, 'live_status', user.uid);
            const newAcceptingState = !acceptingNewRequests;
            
            if (newAcceptingState) {
                // Resuming - clear pause timestamp
                await updateDoc(liveStatusRef, {
                    accepting_new_requests: true,
                    requests_paused_at: null
                });
                setAcceptingNewRequests(true);
                setRequestsPausedAt(null);
                toast({ 
                    title: "Resuming New Requests", 
                    description: "You're now accepting new song requests again." 
                });
            } else {
                // Pausing - set pause timestamp
                const pauseTimestamp = new Date();
                await updateDoc(liveStatusRef, {
                    accepting_new_requests: false,
                    requests_paused_at: serverTimestamp()
                });
                setAcceptingNewRequests(false);
                setRequestsPausedAt(pauseTimestamp);
                toast({ 
                    title: "New Requests Paused", 
                    description: "You're no longer accepting new requests. Complete existing requests before going offline." 
                });
            }
        } catch (error) {
            console.error('Error toggling accepting requests:', error);
            toast({ 
                title: "Error", 
                description: "Could not update request status.", 
                variant: "destructive" 
            });
        }
    };
    
    // Memoized calculations
    const pendingRequests = useMemo(() => {
        // Use trial requests if in trial mode
        const requestsToUse = isTrialMode ? trialRequests : requests;

        // CRITICAL:
        //  - Any real, paid request that is NOT 'played' must appear in the incoming feed
        //  - Some legacy or webhook flows may set status to values other than strict 'pending'
        //    (e.g. undefined, 'approved', 'completed', 'paid'), so we treat all non-'played'
        //    statuses as pending for UI purposes.
        let pending = requestsToUse.filter(req => {
            const status = (req.status || '').toLowerCase();

            // Played requests are never "incoming"
            if (status === 'played') return false;

            // For financial integrity we still prefer paid requests,
            // but DO NOT hide based purely on unfamiliar statuses.
            // If you want to enforce "only paid", uncomment the next line:
            // if (!req.payment_intent_id && !isTrialMode) return false;

            return true;
        });
        
        // If not accepting new requests, filter out requests created after pause timestamp
        if (!acceptingNewRequests && requestsPausedAt && !isTrialMode) {
            pending = pending.filter(req => {
                if (req.is_admin_demo === true) return true;
                const createdAt = req.created_at?.toDate ? req.created_at.toDate() : new Date(req.created_at);
                return createdAt <= requestsPausedAt;
            });
        }
        
        console.log("📋 Pending requests filtered:", pending.length, pending.map(r => ({ id: r.id, status: r.status, song_title: r.song_title })));
        return pending;
    }, [requests, trialRequests, isTrialMode, acceptingNewRequests, requestsPausedAt]);

    const playedRequests = useMemo(() => {
        // Use trial requests if in trial mode
        const requestsToUse = isTrialMode ? trialRequests : requests;
        const played = requestsToUse.filter(req => req.status === 'played');
        console.log("✅ Played requests filtered:", played.length, played.map(r => ({ id: r.id, status: r.status, song_title: r.song_title })));
        return played;
    }, [requests, trialRequests, isTrialMode]);

    // Group pending requests by song and calculate totals
    const groupedPendingRequests = useMemo(() => {
        const grouped = pendingRequests.reduce((acc, request) => {
            const songId = request.song_id ? request.song_id : request.id;
            if (!acc[songId]) {
                acc[songId] = {
                    song_id: songId,
                    song_title: request.song_title,
                    total_amount: 0,
                    total_song_price: 0,
                    total_tips: 0,
                    request_count: 0,
                    all_request_ids: [],
                    requests: []
                };
            }
            acc[songId].total_amount += (request.amount_paid || 0);
            // Calculate song price (amount_paid - tip)
            const songPrice = (request.amount_paid || 0) - (request.tip || 0);
            acc[songId].total_song_price += songPrice;
            acc[songId].total_tips += (request.tip || 0);
            acc[songId].request_count += 1;
            acc[songId].all_request_ids.push(request.id);
            acc[songId].requests.push(request);
            return acc;
        }, {});

        return Object.values(grouped).sort((a, b) => b.total_amount - a.total_amount || a.song_title.localeCompare(b.song_title));
    }, [pendingRequests]);

    const totalEarnings = useMemo(() => {
        // CRITICAL: Exclude trial requests from financial calculations
        // CRITICAL: Only count requests that went through Stripe payment (have payment_intent_id)
        // "Mark As Played" requests (without payment_intent_id) should NOT be counted as paid
        const realRequests = requests.filter(req => !req.is_trial && req.payment_intent_id);
        return realRequests.reduce((sum, req) => {
            // Only count song prices, not tips
            if (req.is_tip_only) {
                return sum; // Skip tip-only requests
            }
            // For song requests, amount_paid includes price + tip, so subtract tip
            const songPrice = (req.amount_paid || 0) - (req.tip || 0);
            return sum + songPrice;
        }, 0);
    }, [requests]);

    const totalTips = useMemo(() => {
        // CRITICAL: Exclude trial mode from financial calculations
        if (isTrialMode || !isLive || !sessionId) {
            return 0; // No tips when offline or in trial mode
        }
        
        // CRITICAL: Exclude trial requests and tips from financial calculations
        const realTips = tips.filter(tip => !tip.is_trial);
        const realRequests = requests.filter(req => !req.is_trial);
        
        // Calculate tips from the tips collection for current session
        const tipsFromTipsCollection = realTips.reduce((sum, tip) => sum + (tip.amount || 0), 0);
        
        // Calculate tips from song requests for current session
        const tipsFromRequests = realRequests.reduce((sum, req) => {
            if (req.is_tip_only) {
                // Tip-only requests (from tip jar)
                return sum + (req.amount_paid || 0);
            } else {
                // Tips added to song requests
                return sum + (req.tip || 0);
            }
        }, 0);
        
        return tipsFromTipsCollection + tipsFromRequests;
    }, [tips, requests, isLive, sessionId, isTrialMode]);


    // Timer effect
    useEffect(() => {
        if (!sessionEndTime) {
            setTimeLeft('');
            return;
        }

        const timer = setInterval(() => {
            const now = new Date();
            const timeRemaining = sessionEndTime - now;
            
            if (timeRemaining <= 0) {
                setTimeLeft('Session Ended');
                endLiveSession();
            } else {
                const minutes = Math.floor(timeRemaining / 60000);
                const seconds = Math.floor((timeRemaining % 60000) / 1000);
                setTimeLeft(`${minutes}:${seconds.toString().padStart(2, '0')}`);
            }
        }, 1000);

        return () => clearInterval(timer);
    }, [sessionEndTime]);



    if (authLoading) {
        return (
            <div className="min-h-screen bg-gray-900 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
                    <p className="text-gray-400">Loading...</p>
                </div>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="flex items-center justify-center h-full">
                <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
            </div>
        );
    }

    return (
        <>
            <Helmet>
                <title>Live Dashboard - Your Song Request</title>
            </Helmet>
            
            {/* Mobile-optimized Live Dashboard */}
            {mobile.isMobile && (
                <MobileLiveDashboard
                    isLive={(isLive || isTrialMode) && trialStep !== 8}
                    sessionData={{
                        timeLeft: timeLeft,
                        sessionStartTime: sessionStartTime,
                        sessionEndTime: sessionEndTime,
                        sessionId: sessionId,
                        totalEarnings: isTrialMode ? 0 : requests
                            .filter(req => req.session_id === sessionId && req.payment_intent_id) // Only count paid requests
                            .reduce((sum, req) => sum + (req.amount_paid || 0), 0),
                        totalTips: isTrialMode ? 0 : totalTips
                    }}
                    requests={isTrialMode ? trialRequests : requests}
                    tips={tips}
                    songs={songs}
                    setlists={setlists}
                    profile={profile}
                    activePlaylistId={activePlaylistId}
                    user={user}
                    isTrialMode={isTrialMode}
                    trialInstructionText={trialInstructionText}
                    isTypingInstruction={isTypingInstruction}
                    showTrialDemoModal={showTrialDemoModal}
                    setShowTrialDemoModal={setShowTrialDemoModal}
                    showTrialPrompt={showTrialPrompt}
                    setShowTrialPrompt={setShowTrialPrompt}
                    showTrialPillButton={showTrialPillButton}
                    trialPillTextIndex={trialPillTextIndex}
                    hasGoneLiveBefore={hasGoneLiveBefore}
                    daysSinceLastLive={daysSinceLastLive}
                    onStartTrialDemo={startTrialDemo}
                    highlightMarkAsPlayedSongId={highlightMarkAsPlayedSongId}
                    highlightRequestCount={highlightRequestCount}
                    goLivePillPulse={goLivePillPulse}
                    showTrialTipPopup={showTrialTipPopup}
                    trialTipData={trialTipData}
                    setShowTrialTipPopup={setShowTrialTipPopup}
                    onMarkAsPlayed={(request) => {
                        // Handle mark as played logic
                        console.log('📱 Mobile onMarkAsPlayed called with:', request);
                        console.log('📱 Request structure:', {
                            hasAllRequestIds: !!request.all_request_ids,
                            allRequestIds: request.all_request_ids,
                            hasId: !!request.id,
                            id: request.id,
                            songTitle: request.song_title
                        });
                        // This should call the existing markAsPlayed function
                        if (request && (request.all_request_ids || request.id)) {
                            markAsPlayed(request);
                        } else {
                            console.error('📱 Invalid request object for markAsPlayed:', request);
                        }
                    }}
                    onMarkAsSkipped={(request) => {
                        // Handle mark as skipped logic
                        console.log('Mark as skipped:', request);
                        // This should call a markAsSkipped function
                        if (request && request.id) {
                            // markAsSkipped(request.id);
                            console.log('Skipped request:', request.id);
                        }
                    }}
                    onSelectSetlist={(setlist) => {
                        // Handle setlist selection
                        if (setlist.id === 'all') {
                            clearSetlist();
                        } else {
                            selectSetlist(setlist.id);
                        }
                    }}
                    onNavigateToCatalogue={() => {
                        // Navigate to catalogue management with setlists tab
                        navigate('/admin/catalogue?tab=setlists');
                    }}
                    onNavigateToSongs={() => {
                        // Navigate to catalogue management with songs tab
                        navigate('/admin/catalogue?tab=songs');
                    }}
                    onGoLive={(duration) => {
                        // Call the context goLive function directly
                        goLive('', user?.uid, duration || 60);
                    }}
                    onShowStartModal={() => {
                        setShowStartModal(true);
                    }}
                    showSessionComplete={showSessionComplete}
                    sessionCompleteData={sessionCompleteData}
                    onCloseSessionComplete={() => setShowSessionComplete(false)}
                    acceptingNewRequests={acceptingNewRequests}
                    requestsPausedAt={requestsPausedAt}
                    onToggleAcceptingRequests={handleToggleAcceptingRequests}
                />
            )}
            
            {/* Desktop Live Dashboard */}
            {!mobile.isMobile && (
                <main className="w-full h-full p-6">
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
                    {/* Header */}
                    <div className="flex items-center justify-between mb-6">
                        <div className="flex items-center space-x-4">
                            <h1 className={`${mobile.isMobile ? 'text-xl' : 'text-2xl'} font-bold text-white`}>Live Dashboard</h1>
                        </div>
                        <div className="flex items-center space-x-3">
                            <UserNotificationCenter />
                        </div>
                        {(showTour ? tourData.isLive : isLive) && (
                            <div className="flex items-center space-x-3">
                                <div className="px-2 py-1 rounded-md bg-green-900/40 border border-green-700/40 text-green-300 font-mono text-sm flex items-center">
                                    <Timer className="w-4 h-4 mr-1" /> {showTour ? tourData.timeLeft : (timeLeft || '—:—')}
                                </div>
                                <Button size="sm" variant="outline" className="h-8 border-gray-600 text-gray-200 hover:bg-gray-700" onClick={() => {
                                    const now = new Date();
                                    const remainingMs = (sessionEndTime ? sessionEndTime.getTime() : now.getTime()) - now.getTime();
                                    const mins = Math.max(1, Math.ceil(remainingMs / 60000));
                                    setEditTimerMinutes(mins);
                                    setShowEditTimerModal(true);
                                }}>
                                    Edit
                                </Button>
                            </div>
                        )}
                    </div>

                    {/* Live Status Controls with Countdown Timer */}
                    <LiveStatusControls 
                        sidebarCollapsed={false} 
                        onTriggerSongWizard={() => {
                            if (isAddSongWizardCompleted(profile, songs.length)) {
                                navigate('/admin/catalogue');
                            } else {
                                setShowSongWizard(true);
                            }
                        }}
                        onGoLive={startLiveSession}
                        songs={songs}
                        pendingRequestsCount={pendingRequests.length}
                        isTrialMode={isTrialMode}
                    />

                    {/* Stop Receiving New Requests Button - Desktop */}
                    {isLive && !isTrialMode && pendingRequests.length >= 1 && (
                        <motion.div
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.3 }}
                            className="mb-4"
                        >
                            <Button
                                onClick={handleToggleAcceptingRequests}
                                className={`w-full ${!acceptingNewRequests ? 'bg-orange-600 hover:bg-orange-700' : 'bg-yellow-600 hover:bg-yellow-700'} text-white font-semibold py-3 rounded-lg shadow-lg`}
                            >
                                {!acceptingNewRequests ? 'Resume New Requests' : 'Pause New Requests'}
                            </Button>
                        </motion.div>
                    )}

                    {/* Share Your Public Page (QR + URL) or Offline Countdown */}
                    {profile?.url_slug && (showOfflineCountdown || (showTour ? tourData.showShareSection : showShareSection)) && !isTrialMode && (
                        <motion.div
                            initial={{ opacity: 1, height: 'auto' }}
                            animate={{ opacity: isTrialMode ? 0 : 1, height: isTrialMode ? 0 : 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.5, ease: 'easeInOut' }}
                            style={{ overflow: 'hidden' }}
                        >
                            <Card ref={shareSectionRef} className="mb-6 bg-gray-800/50 border-gray-700">
                            {showOfflineCountdown ? (
                                // Offline Countdown Card
                                <CardContent className="p-6">
                                    <div className="flex items-center justify-between">
                                        {/* Countdown Section */}
                                        <div className="flex items-center space-x-4">
                                            <div className="flex items-center space-x-2">
                                                <Radio className="w-5 h-5 text-orange-400" />
                                                <span className="text-white font-semibold">Going Offline in</span>
                                            </div>
                                            <div className="flex items-center space-x-1">
                                                {[5, 4, 3, 2, 1].map((num) => (
                                                    <div
                                                        key={num}
                                                        className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all duration-300 ${
                                                            offlineCountdown >= num
                                                                ? 'bg-orange-500 text-white'
                                                                : 'bg-orange-500/30 text-orange-200/50'
                                                        }`}
                                                    >
                                                        {num}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                        
                                        {/* Progress Bar */}
                                        <div className="flex-1 mx-6">
                                            <div className="w-full bg-orange-500/30 rounded-full h-2">
                                                <div 
                                                    className="bg-orange-500 h-2 rounded-full transition-all duration-1000 ease-linear"
                                                    style={{ width: `${(offlineCountdown / 5) * 100}%` }}
                                                ></div>
                                            </div>
                                        </div>
                                        
                                                                            {/* Undo Button */}
                                    <div className="flex items-center">
                                        <Button
                                            onClick={cancelOfflineCountdown}
                                            size="sm"
                                            className="bg-green-600 hover:bg-green-700 text-white font-medium"
                                        >
                                            Stay Live
                                        </Button>
                                    </div>
                                    </div>
                                </CardContent>
                            ) : (
                                // Normal Share Your Public Page Card
                                <>
                                    <CardHeader>
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <CardTitle className="text-white">Share Your Public Page</CardTitle>
                                                <CardDescription className="text-gray-400">Let your audience scan the QR code or copy your URL.</CardDescription>
                                            </div>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={toggleShareSection}
                                                className="text-gray-400 hover:text-white"
                                            >
                                                {showShareSection ? <X className="w-4 h-4" /> : <Info className="w-4 h-4" />}
                                            </Button>
                                        </div>
                                    </CardHeader>
                                    {showShareSection && (
                                        <CardContent>
                                            <div className={`${mobile.isMobile ? 'flex-col space-y-4' : 'flex items-center gap-6 flex-wrap'}`}>
                                                <div className="flex flex-col items-center gap-2">
                                                    <div className="p-3 bg-black/40 rounded-lg border border-gray-700">
                                                        <QRCode value={publicUrl || ''} size={mobile.isMobile ? 100 : 120} />
                                                    </div>
                                                    {profile?.url_slug && (
                                                        <Button
                                                            type="button"
                                                            variant="outline"
                                                            size="sm"
                                                            onClick={handleDownloadQrPng}
                                                            disabled={qrDownloadBusy}
                                                            aria-busy={qrDownloadBusy}
                                                            className="border-gray-600 text-gray-200 hover:bg-gray-700"
                                                        >
                                                            {qrDownloadBusy ? (
                                                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                                            ) : (
                                                                <Download className="w-4 h-4 mr-2" />
                                                            )}
                                                            Download QR
                                                        </Button>
                                                    )}
                                                </div>
                                                <div className={`${mobile.isMobile ? 'w-full' : 'flex-1 min-w-[260px]'}`}>
                                                    <Label className="text-gray-300">Your Public URL</Label>
                                                    <div className="mt-2 flex items-center gap-2">
                                                    <Input readOnly value={publicUrl || ''} className="bg-gray-900 border-gray-700 text-gray-200" />
                                                        <Button
                                                            type="button"
                                                            variant="outline"
                                                            className="border-gray-600 text-gray-200 hover:bg-gray-700"
                                                            onClick={() => publicUrl && navigator.clipboard.writeText(publicUrl)}
                                                        >
                                                            <Copy className="w-4 h-4" />
                                                        </Button>
                                                    </div>
                                                </div>
                                            </div>
                                        </CardContent>
                                    )}
                                </>
                            )}
                        </Card>
                        </motion.div>
                    )}

                    {/* Incoming Requests (single box, always visible) */}
                    <Card ref={incomingRequestsRef} className="bg-gray-800/50 border-gray-700 mb-6">
                        <CardHeader>
                            <CardTitle className="text-white flex items-center">
                                <ListMusic className="w-5 h-5 mr-2 text-blue-400" />
                                Incoming Requests
                                {isTrialMode && (
                                    <span className="ml-2 px-2 py-1 bg-yellow-600/20 text-yellow-400 text-xs rounded-full">Trial Demo</span>
                                )}
                            </CardTitle>
                            <CardDescription className="text-gray-400">Latest song requests will appear here.</CardDescription>
                        </CardHeader>
                        <CardContent>
                            {/* Trial Mode Instructional Text - Large Modal Style */}
                            {isTrialMode && trialInstructionText && (
                                <AnimatePresence>
                                    <motion.div
                                        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9999] flex items-center justify-center p-4"
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        exit={{ opacity: 0 }}
                                    >
                                        <motion.div
                                            className="relative rounded-2xl p-[2px] max-w-lg w-full"
                                            style={{
                                                background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.6), rgba(6, 182, 212, 0.6), rgba(59, 130, 246, 0.6))'
                                            }}
                                            initial={{ scale: 0.8, opacity: 0 }}
                                            animate={{ scale: 1, opacity: 1 }}
                                            exit={{ scale: 0.8, opacity: 0 }}
                                            transition={{ type: "spring", damping: 25, stiffness: 300 }}
                                        >
                                            <div 
                                                className="bg-gray-900/95 rounded-2xl p-8 w-full h-full"
                                                style={{
                                                    background: 'linear-gradient(135deg, rgba(17, 24, 39, 0.8) 0%, rgba(31, 41, 55, 0.8) 100%)',
                                                    boxShadow: '0 0 40px rgba(59, 130, 246, 0.3), inset 0 0 20px rgba(59, 130, 246, 0.1)'
                                                }}
                                            >
                                                <div className="text-center space-y-6">
                                                    <div className="text-white font-semibold text-xl leading-relaxed whitespace-pre-line">
                                                        {trialInstructionText}
                                                        {isTypingInstruction && <span className="animate-pulse text-blue-400">|</span>}
                                                    </div>
                                                </div>
                                            </div>
                                        </motion.div>
                                    </motion.div>
                                </AnimatePresence>
                            )}
                            
                            {(showTour ? tourData.mockRequests : pendingRequests).length === 0 ? (
                                <div className="text-center py-8">
                                    <ListMusic className="w-12 h-12 text-gray-500 mx-auto mb-3" />
                                    <p className="text-gray-400 mb-4">{(showTour ? tourData.isLive : (isLive || isTrialMode)) ? 'Waiting for requests...' : 'Go live to start receiving requests.'}</p>
                                    {(showTour ? tourData.isLive : (isLive || isTrialMode)) && (
                                        <p className="text-gray-400 text-sm">Share your Public Page and have your audience request songs from your catalogue!</p>
                                    )}
                                    {!(showTour ? tourData.isLive : (isLive || isTrialMode)) && (
                                        <div className="flex flex-col items-center space-y-3">
                                            <Button
                                                onClick={() => {
                                                    console.log('🔍 DEBUG: Go Live button clicked');
                                                    // Show the start modal for duration selection
                                                    setShowStartModal(true);
                                                    console.log('🔍 DEBUG: showStartModal set to true');
                                                }}
                                                className="bg-blue-600 hover:bg-blue-700 text-white font-medium px-8 py-3"
                                                size="lg"
                                            >
                                                <Radio className="w-4 h-4 mr-2" />
                                                Go Live
                                            </Button>
                                            {/* Show Trial Demo button for ALL users */}
                                            {songs.length >= 3 && (
                                                <Button
                                                    onClick={() => {
                                                        localStorage.removeItem('trial_demo_dismissed');
                                                        setShowTrialDemoModal(true);
                                                    }}
                                                    variant="outline"
                                                    className="border-yellow-600 text-yellow-400 hover:bg-yellow-600/10"
                                                    size="sm"
                                                >
                                                    {hasGoneLiveBefore && daysSinceLastLive !== null && daysSinceLastLive > 7
                                                        ? "🔄 Need a Refresher? Try the Demo!"
                                                        : "🎵 First Time Going Live? Try the Demo!"}
                                                </Button>
                                            )}
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="space-y-0">
                                    {(showTour ? tourData.mockRequests : groupedPendingRequests.slice(0, 5)).map((groupedRequest, index) => (
                                        <motion.div 
                                            key={showTour ? index : groupedRequest.song_id} 
                                            initial={{ opacity: 0, y: -10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ duration: 0.5, delay: isTrialMode && index === 0 ? 0 : 0 }}
                                            className="relative py-4 px-3 border-b border-gray-600/30 last:border-b-0 hover:bg-gray-700/20 transition-colors"
                                        >
                                            {/* Top border for first item */}
                                            {index === 0 && <div className="absolute top-0 left-0 right-0 h-px bg-gray-600/30"></div>}
                                            
                                            <div className="flex items-start justify-between">
                                                <div className="flex-1 min-w-0">
                                                    {/* Song Title */}
                                                    <h4 className="text-white font-semibold text-lg mb-1 truncate">
                                                        {showTour ? groupedRequest.song : capitalizeWords(groupedRequest.song_title)}
                                                    </h4>
                                                    
                                                    {/* Request Info Row */}
                                                    <div className="flex items-center space-x-4 text-sm mb-2">
                                                        <span className="text-gray-400">
                                                            {showTour ? `Requested by ${groupedRequest.requester}` : (
                                                                groupedRequest.request_count > 1 ? (
                                                                    <div className="flex items-center space-x-1">
                                                                        <span className={isTrialMode && highlightRequestCount === groupedRequest.song_id ? 'font-bold text-yellow-400 drop-shadow-[0_0_8px_rgba(234,179,8,0.8)] animate-pulse' : ''}>
                                                                            {groupedRequest.request_count}
                                                                        </span>
                                                                        <Users className={`w-3 h-3 ${isTrialMode && highlightRequestCount === groupedRequest.song_id ? 'text-yellow-400 drop-shadow-[0_0_8px_rgba(234,179,8,0.8)] animate-pulse' : ''}`} />
                                                                        <span>request{groupedRequest.request_count > 1 ? 's' : ''}</span>
                                                                    </div>
                                                                ) : (
                                                                    `${groupedRequest.request_count} request${groupedRequest.request_count > 1 ? 's' : ''}`
                                                                )
                                                            )}
                                                        </span>
                                                    </div>
                                                    
                                                    {/* Price Info Row */}
                                                    {!showTour && groupedRequest.total_amount > 0 && (
                                                        <div className="flex items-center space-x-3 flex-wrap">
                                                            <div className="flex items-center space-x-1 bg-yellow-400/10 px-2 py-1 rounded-full border border-yellow-400/20">
                                                                <span className="text-yellow-400 font-semibold text-base">
                                                                    {format(groupedRequest.total_song_price)}
                                                                </span>
                                                            </div>
                                                            {groupedRequest.total_tips > 0 && (
                                                                <div className="flex items-center space-x-1 bg-red-400/10 px-2 py-1 rounded-full border border-red-400/20">
                                                                    <Heart className="w-3 h-3 text-red-400" />
                                                                    <span className="text-red-400 font-medium text-sm">
                                                                        +{format(groupedRequest.total_tips)} tip
                                                                    </span>
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}
                                                    {/* Artist Name */}
                                                    {!showTour && groupedRequest.requests && groupedRequest.requests.length > 0 && (
                                                        <p className="text-gray-400 text-xs mt-1">
                                                            by {capitalizeWords(groupedRequest.requests[0].original_artist || 'Unknown Artist')}
                                                        </p>
                                                    )}
                                                </div>
                                                
                                                {/* Mark As Played Button */}
                                                <div className="flex-shrink-0 ml-4">
                                                    <Button 
                                                        ref={index === 0 ? markAsPlayedButtonRef : null}
                                                        size="sm" 
                                                        onClick={() => {
                                                            console.log("🚨 BUTTON CLICKED - About to call markAsPlayed");
                                                            console.log("🚨 markAsPlayed function type:", typeof markAsPlayed);
                                                            console.log("🚨 groupedRequest:", groupedRequest);
                                                            if (showTour) return null;
                                                            markAsPlayed(groupedRequest);
                                                        }} 
                                                        className={`${showTour && groupedRequest.status === 'played' ? 'bg-gray-600 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700'} px-3 py-1.5 text-xs font-medium ${
                                                            isTrialMode && highlightMarkAsPlayedSongId === groupedRequest.song_id 
                                                                ? 'animate-pulse drop-shadow-[0_0_12px_rgba(34,197,94,0.9)] shadow-[0_0_20px_rgba(34,197,94,0.6)] ring-2 ring-green-400 ring-offset-2 ring-offset-gray-900' 
                                                                : ''
                                                        }`}
                                                        disabled={showTour && groupedRequest.status === 'played'}
                                                    >
                                                        <Check className="w-3 h-3 mr-1" />
                                                        {showTour && groupedRequest.status === 'played' ? 'Played' : 'Mark As Played'}
                                                    </Button>
                                                </div>
                                            </div>
                                        </motion.div>
                                    ))}
                                    {groupedPendingRequests.length > 5 && (
                                        <p className="text-center text-gray-400 text-sm">Showing latest 5</p>
                                    )}
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    {/* Trial Demo Prompt - Middle of Dashboard */}
                    {showTrialPrompt && !isLive && !isTrialMode && (
                        <Card className="mb-6 bg-gradient-to-r from-blue-900/30 to-purple-900/30 border-blue-700/50">
                            <CardContent className="p-6">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center space-x-4 flex-1">
                                        <div className="text-4xl">🎵</div>
                                        <div className="flex-1">
                                            <h3 className="text-white font-semibold text-lg mb-1">
                                                {hasGoneLiveBefore && daysSinceLastLive !== null && daysSinceLastLive > 7
                                                    ? "Need a refresher? Take a quick trial!"
                                                    : "First Time Going Live? Try the Demo!"}
                                            </h3>
                                            <p className="text-gray-300 text-sm">
                                                See how song requests work with an interactive demo using your own songs.
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                        <Button
                                            onClick={() => {
                                                localStorage.removeItem('trial_demo_dismissed');
                                                setShowTrialDemoModal(true);
                                            }}
                                            className="bg-blue-600 hover:bg-blue-700 text-white"
                                        >
                                            Start Demo
                                        </Button>
                                        <Button
                                            onClick={() => {
                                                setShowTrialPrompt(false);
                                                // Store permanent dismissal per artist
                                                if (user?.uid) {
                                                    localStorage.setItem(`trial_demo_dismissed_permanent_${user.uid}`, 'true');
                                                }
                                                setShowTrialPillButton(false);
                                                setShowTrialDemoModal(false);
                                                toast({
                                                    title: "Demo Hidden",
                                                    description: "The demo prompt and pill are hidden for this artist.",
                                                });
                                            }}
                                            variant="ghost"
                                            className="text-gray-400 hover:text-white"
                                        >
                                            Do not show again
                                        </Button>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    )}

                    {/* Stats Section */}
                    <Card className="mb-6 bg-gray-800/50 border-gray-700">
                        <CardHeader>
                            <div className="flex items-center justify-between">
                                <div>
                                    <CardTitle className="text-white">Stats</CardTitle>
                                    <CardDescription className="text-gray-400">Session performance metrics</CardDescription>
                                </div>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={toggleStatsSection}
                                    className="text-gray-400 hover:text-white"
                                >
                                    {showStatsSection ? <X className="w-4 h-4" /> : <Info className="w-4 h-4" />}
                                </Button>
                            </div>
                        </CardHeader>
                        {showStatsSection && (
                            <CardContent>
                                <div className={`grid ${mobile.isMobile ? 'grid-cols-2' : 'grid-cols-1 md:grid-cols-4'} gap-4`}>
                                    <Card className="bg-gray-700/50 border-gray-600">
                                        <CardContent className="p-4">
                                            <div className="flex items-center space-x-2">
                                                <ListMusic className="w-5 h-5 text-blue-400" />
                                                <span className="text-white font-semibold">{groupedPendingRequests.length}</span>
                                                <span className="text-gray-400">Pending</span>
                                            </div>
                                        </CardContent>
                                    </Card>
                                    <Card className="bg-gray-700/50 border-gray-600">
                                        <CardContent className="p-4">
                                            <div className="flex items-center space-x-2">
                                                <Check className="w-5 h-5 text-green-400" />
                                                <span className="text-white font-semibold">{playedRequests.length}</span>
                                                <span className="text-gray-400">Played</span>
                                            </div>
                                        </CardContent>
                                    </Card>
                                    <Card className="bg-gray-700/50 border-gray-600">
                                        <CardContent className="p-4">
                                            <div className="flex items-center space-x-2">
                                                <User className="w-5 h-5 text-purple-400" />
                                                <span className="text-white font-semibold">{format(totalEarnings)}</span>
                                                <span className="text-gray-400">
                                                  {profile?.agent_id && profile?.agent_payout_preference === '80_20' ? 'Gross' : 'Earnings'}
                                                </span>
                                            </div>
                                        </CardContent>
                                    </Card>
                                    <Card className="bg-gray-700/50 border-gray-600">
                                        <CardContent className="p-4">
                                            <div className="flex items-center space-x-2">
                                                <Tag className="w-5 h-5 text-yellow-400" />
                                                <span className="text-white font-semibold">{format(totalTips)}</span>
                                                <span className="text-gray-400">Tips</span>
                                            </div>
                                        </CardContent>
                                    </Card>
                                </div>
                            </CardContent>
                        )}
                    </Card>

                    {/* Requests Section */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* Setlist Selection */}
                        <Card ref={setlistsSectionRef} className="bg-gray-800/50 border-gray-700">
                            <CardHeader>
                                <CardTitle className="text-white flex items-center">
                                    <ListMusic className="w-5 h-5 mr-2 text-green-400" />
                                    Active Setlist
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                {playlists.length === 0 ? (
                                    <div className="text-center py-8">
                                        <ListMusic className="w-16 h-16 text-gray-500 mx-auto mb-4" />
                                        <p className="text-gray-400 mb-4">No setlists created yet</p>
                                        <p className="text-gray-500 text-sm mb-4">
                                            Setlists let you control which songs your audience can request during live sessions.
                                        </p>
                                        <Button
                                            onClick={() => navigate('/admin/catalogue?tab=setlists')}
                                            className="bg-orange-600 hover:bg-orange-700"
                                        >
                                            Create Your First Setlist
                                        </Button>
                                    </div>
                                ) : (
                                    <div className="space-y-3">
                                        <div className="flex items-center justify-between p-3 bg-gray-700/50 rounded-lg">
                                            <div className="flex-1">
                                                <h4 className="text-white font-medium">All Songs Available</h4>
                                                <p className="text-gray-400 text-sm">Your audience can request any song from your catalogue</p>
                                            </div>
                                            <Button
                                                size="sm"
                                                variant={!activePlaylistId ? "default" : "outline"}
                                                onClick={() => clearSetlist()}
                                                className={!activePlaylistId ? "bg-orange-600 hover:bg-orange-700" : "border-gray-600 text-gray-300 hover:bg-gray-700"}
                                            >
                                                {!activePlaylistId ? "Active" : "Select"}
                                            </Button>
                                        </div>
                                        {playlists.map((playlist) => (
                                            <div key={playlist.id} className="flex items-center justify-between p-3 bg-gray-700/50 rounded-lg">
                                                <div className="flex-1">
                                                    <h4 className="text-white font-medium">{playlist.name}</h4>
                                                    <p className="text-gray-400 text-sm">
                                                        {playlist.songs?.length || 0} songs • {playlist.description || 'No description'}
                                                    </p>
                                                </div>
                                                <Button
                                                    size="sm"
                                                    variant={activePlaylistId === playlist.id ? "default" : "outline"}
                                                    onClick={() => selectSetlist(playlist.id)}
                                                    className={activePlaylistId === playlist.id ? "bg-orange-600 hover:bg-orange-700" : "border-gray-600 text-gray-300 hover:bg-gray-700"}
                                                >
                                                    {activePlaylistId === playlist.id ? "Active" : "Select"}
                                                </Button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </CardContent>
                        </Card>

                        {/* Played Requests */}
                        <Card className="bg-gray-800/50 border-gray-700">
                            <CardHeader>
                                <CardTitle className="text-white flex items-center">
                                    <Check className="w-5 h-5 mr-2 text-green-400" />
                                    Played Requests ({playedRequests.length})
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-4">
                                    {/* Search Section - Always Visible */}
                                    <div ref={manualSearchRef} className="flex items-center justify-between p-3 bg-gray-700/30 rounded-lg border border-gray-600/50">
                                        <div className="flex-1">
                                            <p className="text-gray-300 text-sm font-medium">Mark a song as played that hasn't been requested?</p>
                                        </div>
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => setShowSongSearchModal(true)}
                                            className="border-gray-600 text-gray-300 hover:bg-gray-700"
                                        >
                                            Search
                                        </Button>
                                    </div>

                                    {/* Played Requests List */}
                                    {playedRequests.length === 0 ? (
                                        <div className="text-center py-8">
                                            <Check className="w-16 h-16 text-gray-500 mx-auto mb-4" />
                                            <p className="text-gray-400">No played requests, yet</p>
                                        </div>
                                    ) : (
                                        <div className="space-y-3">
                                            {playedRequests.slice(0, 10).map((request) => (
                                                <div key={request.id} className="flex items-center justify-between p-3 bg-gray-700/50 rounded-lg">
                                                    <div className="flex-1">
                                                        <h4 className="text-white font-medium line-through text-gray-500">{capitalizeWords(request.song_title)}</h4>
                                                        {request.original_artist && (
                                                            <p className="text-gray-500 text-xs">by {capitalizeWords(request.original_artist)}</p>
                                                        )}
                                                        <p className="text-gray-400 text-sm">by {request.requester_name}</p>
                                                        {request.amount_paid > 0 && (
                                                            <p className="text-green-400 text-sm">{format(Number(request.amount_paid) || 0)}</p>
                                                        )}
                                                    </div>
                                                    <div className="flex items-center space-x-2">
                                                        <div className="text-gray-500 text-sm">
                                                            {request.updated_at ? new Date(request.updated_at.toDate()).toLocaleTimeString() : 'N/A'}
                                                        </div>
                                                        <Button
                                                            size="sm"
                                                            variant="outline"
                                                            onClick={() => reEnableSong(request)}
                                                            className="border-gray-600 text-gray-300 hover:bg-gray-700"
                                                        >
                                                            <Undo2 className="w-4 h-4 mr-1" />
                                                            Undo
                                                        </Button>
                                                    </div>
                                                </div>
                                            ))}
                                            {playedRequests.length > 10 && (
                                                <p className="text-center text-gray-400 text-sm">
                                                    Showing last 10 requests
                                                </p>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                </motion.div>
            </main>
            )}

            {/* Start Session Modal - Mobile Optimized */}
            <MobileAnimatedModal
                isOpen={mobile.isMobile && showStartModal}
                onClose={() => setShowStartModal(false)}
                title="Start a live session"
                icon={Radio}
                position="center-high"
                maxWidth="md"
                className="-translate-y-4"
                overlayZIndex={2147483643}
            >
                <div className="space-y-5 pt-1">
                    <p className={ysr2Mobile.pill}>Session length</p>
                    <p className="text-sm text-blue-100/80 leading-relaxed">
                        Default is <span className="text-white font-semibold">60 minutes</span>. Enter any whole number from{' '}
                        <span className="text-white font-medium">5</span> to <span className="text-white font-medium">240</span>. You can extend later from the dashboard.
                    </p>
                    <div>
                        <label htmlFor="startDurationMobile" className="block text-[11px] font-semibold uppercase tracking-[0.18em] text-blue-100/65 mb-2">
                            Minutes
                        </label>
                        <input
                            id="startDurationMobile"
                            type="text"
                            inputMode="numeric"
                            pattern="[0-9]*"
                            autoComplete="off"
                            enterKeyHint="done"
                            value={startDurationDraft}
                            onChange={(e) => setStartDurationDraft(sanitizeSessionDurationDraft(e.target.value))}
                            className="w-full min-h-[52px] rounded-2xl border-2 border-blue-400/55 bg-black/45 px-4 py-3 text-center text-2xl font-semibold tracking-wide text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.07)] placeholder:text-slate-500 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-500/35"
                            placeholder="60"
                        />
                    </div>
                    <div className="flex flex-col gap-2 pt-1">
                        <button
                            type="button"
                            onClick={() => {
                                setShowStartModal(false);
                                startLiveSession(clampSessionDurationMinutes(startDurationDraft));
                            }}
                            className={`w-full ${ysr2Mobile.primaryButton} py-3.5 text-sm font-semibold touch-manipulation`}
                        >
                            Go Live
                        </button>
                        <button
                            type="button"
                            onClick={() => setShowStartModal(false)}
                            className={`w-full ${ysr2Mobile.secondaryButton} py-3.5 text-sm font-semibold touch-manipulation`}
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            </MobileAnimatedModal>
            
            {/* Trial Demo Modal */}
            <Dialog open={showTrialDemoModal} onOpenChange={setShowTrialDemoModal}>
                <DialogContent className="bg-gray-900 border-gray-700 text-white max-w-md">
                    <DialogHeader>
                        <DialogTitle className="text-2xl font-bold text-center">
                            {hasGoneLiveBefore && daysSinceLastLive !== null && daysSinceLastLive > 7
                                ? "🔄 Need a Refresher?"
                                : "🎵 First Time Going Live?"}
                        </DialogTitle>
                        <DialogDescription className="text-center text-gray-300 mt-2">
                            {hasGoneLiveBefore && daysSinceLastLive !== null && daysSinceLastLive > 7
                                ? "Take a quick trial to refresh your memory!"
                                : "Try our interactive demo to see how song requests work!"}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="bg-blue-900/20 border border-blue-700/30 rounded-lg p-4">
                            <h3 className="text-white font-semibold mb-2">What you'll learn:</h3>
                            <ul className="text-gray-300 text-sm space-y-1 list-disc list-inside">
                                <li>How requests appear in your dashboard</li>
                                <li>How to mark songs as played</li>
                                <li>How tips work</li>
                                <li>How multiple requests for the same song work</li>
                            </ul>
                        </div>
                    </div>
                    <DialogFooter className="flex-col space-y-2 sm:flex-row sm:space-y-0 sm:space-x-2">
                        <Button 
                            variant="outline" 
                            onClick={() => {
                                setShowTrialDemoModal(false);
                                // For trial@trial.com, don't save dismissal so they can always access it
                                if (user?.email !== 'trial@trial.com') {
                                    localStorage.setItem('trial_demo_dismissed', 'true');
                                }
                            }}
                            className="w-full sm:w-auto"
                        >
                            Maybe Later
                        </Button>
                        <Button 
                            onClick={() => {
                                console.log('🎬 Start Demo button clicked');
                                startTrialDemo();
                            }}
                            className="bg-blue-600 hover:bg-blue-700 w-full sm:w-auto"
                            disabled={songs.length < 3}
                        >
                            Start Demo!
                        </Button>
                        <Button 
                            variant="ghost" 
                            onClick={() => {
                                setShowTrialDemoModal(false);
                                // Store permanent dismissal per artist
                                if (user?.uid) {
                                    localStorage.setItem(`trial_demo_dismissed_permanent_${user.uid}`, 'true');
                                }
                                setShowTrialPrompt(false);
                                setShowTrialPillButton(false);
                                // Also dismiss for this session
                                if (user?.email !== 'trial@trial.com') {
                                    localStorage.setItem('trial_demo_dismissed', 'true');
                                }
                            }}
                            className="w-full sm:w-auto text-gray-400 hover:text-gray-300 text-sm"
                        >
                            Don't Show Me This Again
                        </Button>
                    </DialogFooter>
                    {songs.length < 3 && (
                        <p className="text-yellow-400 text-xs text-center mt-2">
                            You need at least 3 songs in your catalogue to run the demo.
                        </p>
                    )}
                </DialogContent>
            </Dialog>

            {/* Desktop Start Session Modal */}
            {!mobile.isMobile && (
                <Dialog open={showStartModal} onOpenChange={setShowStartModal}>
                    <DialogContent className="bg-gray-900 border-gray-700 text-white">
                        <DialogHeader>
                            <DialogTitle>Start a Live Session</DialogTitle>
                            <DialogDescription>
                                By default, sessions run for 60 minutes. You can change it below.
                            </DialogDescription>
                        </DialogHeader>
                        <div className="space-y-3">
                            <Label htmlFor="startDuration">Session length (minutes)</Label>
                            <Input
                                id="startDuration"
                                type="text"
                                inputMode="numeric"
                                pattern="[0-9]*"
                                autoComplete="off"
                                value={startDurationDraft}
                                onChange={(e) => setStartDurationDraft(sanitizeSessionDurationDraft(e.target.value))}
                            />
                        </div>
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setShowStartModal(false)}>Cancel</Button>
                            <Button
                                onClick={() => {
                                    setShowStartModal(false);
                                    startLiveSession(clampSessionDurationMinutes(startDurationDraft));
                                }}
                                className="bg-green-600 hover:bg-green-700"
                            >
                                Go Live
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            )}

            {/* Edit Timer Modal */}
            <Dialog open={showEditTimerModal} onOpenChange={setShowEditTimerModal}>
                <DialogContent className="bg-gray-900 border-gray-700 text-white">
                    <DialogHeader>
                        <DialogTitle>Edit Session Timer</DialogTitle>
                        <DialogDescription>Set how many minutes remain in this live session.</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <Label htmlFor="editTimer">Minutes remaining</Label>
                        <Input id="editTimer" type="number" min="1" max="240" value={editTimerMinutes} onChange={(e) => setEditTimerMinutes(Number(e.target.value))} />
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowEditTimerModal(false)}>Cancel</Button>
                        <Button onClick={async () => {
                            try {
                                const mins = Math.max(1, Math.min(240, Number(editTimerMinutes) || 60));
                                const newEnd = new Date(Date.now() + mins * 60000);
                                if (user) {
                                    const liveStatusRef = doc(db, 'live_status', user.uid);
                                    await updateDoc(liveStatusRef, { session_end_time: newEnd });
                                }
                                setShowEditTimerModal(false);
                                toast({ title: 'Timer updated' });
                            } catch (err) {
                                console.error('Error updating timer', err);
                                toast({ title: 'Error', description: 'Could not update timer', variant: 'destructive' });
                            }
                        }} className="bg-blue-600 hover:bg-blue-700">Save</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Song Search Modal */}
            <Dialog open={showSongSearchModal} onOpenChange={setShowSongSearchModal}>
                <DialogContent className="bg-gray-900 border-gray-700 text-white max-w-md">
                    <DialogHeader>
                        <DialogTitle>Mark Song as Played</DialogTitle>
                        <DialogDescription>
                            Search for a song in your catalogue to mark it as played.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <Label htmlFor="songSearch" className="text-gray-300">Search Songs</Label>
                            <Input
                                id="songSearch"
                                type="text"
                                placeholder="Search by song title or artist..."
                                value={songSearchQuery}
                                onChange={(e) => handleSongSearch(e.target.value)}
                                className="bg-gray-800 border-gray-600 text-white"
                            />
                        </div>
                        
                        {filteredSongs.length > 0 && (
                            <div className="max-h-60 overflow-y-auto space-y-2">
                                {filteredSongs.map((song) => (
                                    <div
                                        key={song.id}
                                        className="flex items-center justify-between p-3 bg-gray-800 rounded-lg hover:bg-gray-700 cursor-pointer"
                                        onClick={() => markSongAsPlayedFromSearch(song)}
                                    >
                                        <div>
                                            <h4 className="text-white font-medium">{capitalizeWords(song.title)}</h4>
                                            {(song.artist || song.original_artist) && (
                                                <p className="text-gray-400 text-sm">by {capitalizeWords(song.artist || song.original_artist || 'Unknown Artist')}</p>
                                            )}
                                        </div>
                                        <Button size="sm" className="bg-green-600 hover:bg-green-700">
                                            <Check className="w-4 h-4" />
                                        </Button>
                                    </div>
                                ))}
                            </div>
                        )}
                        
                        {songSearchQuery && filteredSongs.length === 0 && (
                            <p className="text-gray-400 text-center py-4">No songs found matching "{songSearchQuery}"</p>
                        )}
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => {
                            setShowSongSearchModal(false);
                            setSongSearchQuery('');
                            setFilteredSongs([]);
                        }}>
                            Cancel
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Session Complete Modal */}
            <Dialog open={showSessionComplete} onOpenChange={setShowSessionComplete}>
                <DialogContent className="bg-gray-900 border-gray-700 text-white max-w-lg max-h-[90vh] flex flex-col">
                    <DialogHeader className="flex-shrink-0">
                        <DialogTitle className="text-center text-xl font-bold text-green-400">
                            🎉 Session Complete!
                        </DialogTitle>
                        <DialogDescription className="text-center text-gray-300 text-sm">
                            Your live session has ended. Here's your performance summary.
                        </DialogDescription>
                    </DialogHeader>
                    
                    {sessionCompleteData && (
                        <div className="flex-1 overflow-y-auto space-y-4 px-1">
                            {/* Session Duration */}
                            <div className="text-center">
                                <p className="text-gray-400 text-xs">Session Duration</p>
                                <p className="text-lg font-semibold text-white">
                                    {sessionCompleteData.startTime && sessionCompleteData.endTime ? 
                                        `${Math.round((sessionCompleteData.endTime - sessionCompleteData.startTime) / 60000)} minutes` : 
                                        'N/A'
                                    }
                                </p>
                            </div>

                            {/* Metrics Grid - More Compact */}
                            <div className="grid grid-cols-2 gap-3">
                                <motion.div
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.1 }}
                                    className="bg-gray-800 rounded-lg p-3 text-center"
                                >
                                    <p className="text-xl font-bold text-blue-400">{sessionCompleteData.totalRequests}</p>
                                    <p className="text-gray-400 text-xs">Total Requests</p>
                                </motion.div>
                                
                                <motion.div
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.2 }}
                                    className="bg-gray-800 rounded-lg p-3 text-center"
                                >
                                    <p className="text-xl font-bold text-green-400">{sessionCompleteData.playedRequests}</p>
                                    <p className="text-gray-400 text-xs">Songs Played</p>
                                </motion.div>
                                
                                <motion.div
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.3 }}
                                    className="bg-gray-800 rounded-lg p-3 text-center"
                                >
                                    <p className="text-xl font-bold text-purple-400">{format(sessionCompleteData.totalEarnings)}</p>
                                    <p className="text-gray-400 text-xs">Song Earnings</p>
                                </motion.div>
                                
                                <motion.div
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.4 }}
                                    className="bg-gray-800 rounded-lg p-3 text-center"
                                >
                                    <p className="text-xl font-bold text-yellow-400">{format(sessionCompleteData.totalTips)}</p>
                                    <p className="text-gray-400 text-xs">Tips Received</p>
                                </motion.div>
                            </div>

                            {/* Most Requested Song - More Compact */}
                            {sessionCompleteData.mostRequestedSong && (
                                <motion.div
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.45 }}
                                    className="bg-gray-800 rounded-lg p-3"
                                >
                                    <div className="text-center">
                                        <p className="text-gray-400 text-xs mb-2">Most Requested Song</p>
                                        <div className="flex items-center justify-center space-x-2">
                                            <Music className="w-4 h-4 text-pink-400" />
                                            <div>
                                                <p className="text-sm font-semibold text-white">
                                                    {sessionCompleteData.mostRequestedSong.title}
                                                </p>
                                                <p className="text-gray-400 text-xs">
                                                    by {sessionCompleteData.mostRequestedSong.artist}
                                                </p>
                                            </div>
                                            <span className="bg-pink-600 text-white px-2 py-0.5 rounded-full text-xs font-medium">
                                                {sessionCompleteData.mostRequestedSong.count} request{sessionCompleteData.mostRequestedSong.count > 1 ? 's' : ''}
                                            </span>
                                        </div>
                                    </div>
                                </motion.div>
                            )}

                            {/* Total Earnings - More Compact */}
                            <motion.div
                                initial={{ opacity: 0, scale: 0.9 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ delay: 0.5 }}
                                className="bg-gradient-to-r from-green-600 to-blue-600 rounded-lg p-4 text-center"
                            >
                                <p className="text-gray-200 text-xs mb-2">Total Session Earnings</p>
                                <p className="text-2xl font-bold text-white">
                                    {format(sessionCompleteData.totalEarnings + sessionCompleteData.totalTips)}
                                </p>
                            </motion.div>

                            {/* Pending Requests Warning - More Compact */}
                            {sessionCompleteData.pendingRequests > 0 && (
                                <motion.div
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    transition={{ delay: 0.6 }}
                                    className="bg-amber-900/20 border border-amber-700/50 rounded-lg p-3"
                                >
                                    <div className="flex items-center space-x-2">
                                        <Info className="w-4 h-4 text-amber-400 flex-shrink-0" />
                                        <p className="text-amber-200 text-sm">
                                            <strong>Note:</strong> {sessionCompleteData.pendingRequests} request{sessionCompleteData.pendingRequests > 1 ? 's' : ''} were not played during this session.
                                        </p>
                                    </div>
                                </motion.div>
                            )}
                        </div>
                    )}
                    
                    <DialogFooter className="flex-shrink-0 mt-4">
                        <Button 
                            onClick={() => setShowSessionComplete(false)}
                            className="bg-blue-600 hover:bg-blue-700 w-full"
                        >
                            Close Summary
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Dynamic spotlight positioning functions */}
            {(() => {
                // Calculate spotlight position for current tour step
                const calculateSpotlightPosition = () => {
                    if (!showTour) return;

                    let targetRef = null;
                    let instructionOffset = { x: 0, y: 0 };

                    switch (tourStep) {
                        case 0: // Go Live button
                            targetRef = goLiveButtonRef.current;
                            instructionOffset = { x: -340, y: 0 }; // Position to the left since it's on the right side
                            break;
                        case 1: // Share section
                            targetRef = shareSectionRef.current;
                            instructionOffset = { x: 0, y: 120 };
                            break;
                        case 2: // Incoming requests
                            targetRef = incomingRequestsRef.current;
                            instructionOffset = { x: 0, y: 100 };
                            break;
                        case 3: // Mark as played button
                            targetRef = markAsPlayedButtonRef.current;
                            instructionOffset = { x: 0, y: 80 };
                            break;
                        case 4: // Manual search
                            targetRef = manualSearchRef.current;
                            instructionOffset = { x: 0, y: 80 };
                            break;
                        case 5: // Setlists section
                            targetRef = setlistsSectionRef.current;
                            instructionOffset = { x: 0, y: -120 }; // Position above the element instead of below
                            break;
                        case 6: // Go offline button (same as Go Live button since it's the same toggle)
                            targetRef = goLiveButtonRef.current;
                            instructionOffset = { x: -340, y: 0 }; // Position to the left since it's on the right side
                            break;
                    }

                    if (targetRef) {
                        const rect = targetRef.getBoundingClientRect();
                        const padding = 8; // Add some padding around the element
                        
                        setSpotlightPosition({
                            top: rect.top - padding,
                            left: rect.left - padding,
                            width: rect.width + (padding * 2),
                            height: rect.height + (padding * 2)
                        });

                        // Position instruction box next to the element with boundary detection
                        const instructionWidth = 320; // Approximate width of instruction box
                        const instructionHeight = 280; // Approximate height of instruction box
                        const viewportWidth = window.innerWidth;
                        const viewportHeight = window.innerHeight;
                        
                        let instructionLeft = rect.left + instructionOffset.x;
                        let instructionTop = rect.top + instructionOffset.y;
                        
                        // Check if instruction box would go off the right edge
                        if (instructionLeft + instructionWidth > viewportWidth - 20) {
                            // Position to the left of the element instead
                            instructionLeft = Math.max(20, rect.left - instructionWidth - 20);
                        }
                        
                        // Check if instruction box would go off the left edge
                        if (instructionLeft < 20) {
                            instructionLeft = 20;
                        }
                        
                        // Check if instruction box would go off the bottom edge
                        if (instructionTop + instructionHeight > viewportHeight - 20) {
                            // Position above the element instead
                            instructionTop = Math.max(20, rect.top - instructionHeight - 20);
                        }
                        
                        // Check if instruction box would go off the top edge
                        if (instructionTop < 20) {
                            instructionTop = 20;
                        }
                        
                        setInstructionPosition({
                            top: instructionTop,
                            left: instructionLeft
                        });
                    }
                };

                // Update spotlight position when tour step changes
                if (showTour) {
                    // Small delay to ensure DOM is updated
                    setTimeout(calculateSpotlightPosition, 100);
                }

                return null;
            })()}

            {/* Immersive Tour Overlay */}
            {showTour && (
                <div className="fixed inset-0 z-50 pointer-events-none">
                    {/* Tour overlay */}
                    <div className="relative h-full overflow-hidden">
                        {/* Subtle dimming background */}
                        <div className="absolute inset-0 bg-black/40" />
                        
                        {/* Dynamic spotlight effect */}
                        <div 
                            className="absolute pointer-events-auto"
                            style={{
                                top: spotlightPosition.top,
                                left: spotlightPosition.left,
                                width: spotlightPosition.width,
                                height: spotlightPosition.height,
                                boxShadow: tourStep === 5 
                                    ? '0 0 50px 20px rgba(249, 115, 22, 0.5)' 
                                    : tourStep === 6
                                    ? '0 0 50px 20px rgba(239, 68, 68, 0.5)'
                                    : '0 0 50px 20px rgba(59, 130, 246, 0.5)',
                                background: tourStep === 5 
                                    ? 'radial-gradient(circle, rgba(249, 115, 22, 0.3) 0%, transparent 70%)'
                                    : tourStep === 6
                                    ? 'radial-gradient(circle, rgba(239, 68, 68, 0.3) 0%, transparent 70%)'
                                    : 'radial-gradient(circle, rgba(59, 130, 246, 0.3) 0%, transparent 70%)',
                                borderRadius: '8px',
                                zIndex: 10
                            }}
                        />
                        
                        {/* Dynamic instruction box */}
                        <div 
                            className={`absolute pointer-events-auto text-white p-4 rounded-lg shadow-lg max-w-md text-center ${
                                tourStep === 5 ? 'bg-orange-600' : tourStep === 6 ? 'bg-red-600' : 'bg-blue-600'
                            }`}
                            style={{
                                top: instructionPosition.top,
                                left: instructionPosition.left,
                                zIndex: 20
                            }}
                        >
                            {tourStep === 0 && (
                                <>
                                    <h3 className="text-lg font-semibold mb-2">🎤 Step 1: Go Live</h3>
                                    <p className="text-sm mb-3">Click the "GO LIVE" button to start your live session and begin receiving song requests!</p>
                                    <div className="flex justify-center space-x-2">
                                        <Button 
                                            size="sm"
                                            onClick={() => {
                                                setTourData(prev => ({ ...prev, isLive: true, sessionStartTime: new Date() }));
                                                setTourStep(1);
                                            }}
                                            className="bg-white text-blue-600 hover:bg-gray-100"
                                        >
                                            Simulate Going Live
                                        </Button>
                                        <Button 
                                            size="sm"
                                            variant="outline"
                                            onClick={() => {
                                                setShowTour(false);
                                            }}
                                            className="border-white text-white hover:bg-white hover:text-blue-600"
                                        >
                                            Skip Tour
                                        </Button>
                                    </div>
                                </>
                            )}
                            
                            {tourStep === 1 && (
                                <>
                                    <h3 className="text-lg font-semibold mb-2">📱 Step 2: Share Your Page</h3>
                                    <p className="text-sm mb-3">Now share your QR code and URL with your audience so they can request songs!</p>
                                    <div className="flex justify-center space-x-2">
                                        <Button 
                                            size="sm"
                                            onClick={() => {
                                                setTourData(prev => ({ ...prev, showShareSection: false }));
                                                setTourStep(2);
                                            }}
                                            className="bg-white text-green-600 hover:bg-gray-100"
                                        >
                                            Continue
                                        </Button>
                                    </div>
                                </>
                            )}
                            
                            {tourStep === 2 && (
                                <>
                                    <h3 className="text-lg font-semibold mb-2">🎵 Step 3: Manage Requests</h3>
                                    <p className="text-sm mb-3">Great! You're receiving song requests. Click "Mark As Played" when you perform a song.</p>
                                    <div className="flex justify-center space-x-2">
                                        <Button 
                                            size="sm"
                                            onClick={() => {
                                                setTourData(prev => ({ 
                                                    ...prev, 
                                                    mockRequests: [
                                                        { id: 1, song: 'Wonderwall', artist: 'Oasis', requester: 'Sarah M.', status: 'pending' },
                                                        { id: 2, song: 'Hotel California', artist: 'Eagles', requester: 'Mike J.', status: 'pending' }
                                                    ]
                                                }));
                                                setTourStep(3);
                                            }}
                                            className="bg-white text-purple-600 hover:bg-gray-100"
                                        >
                                            Simulate Requests
                                        </Button>
                                    </div>
                                </>
                            )}
                            
                            {tourStep === 3 && (
                                <>
                                    <h3 className="text-lg font-semibold mb-2">✅ Step 4: Mark Songs as Played</h3>
                                    <p className="text-sm mb-3">Click "Mark As Played" on the songs you perform. This moves them to your "Played" list.</p>
                                    <div className="flex justify-center space-x-2">
                                        <Button 
                                            size="sm"
                                            onClick={() => {
                                                setTourData(prev => ({ 
                                                    ...prev, 
                                                    mockRequests: prev.mockRequests.map(req => 
                                                        req.song === 'Wonderwall' ? { ...req, status: 'played' } : req
                                                    )
                                                }));
                                                setTourStep(4);
                                            }}
                                            className="bg-white text-green-600 hover:bg-gray-100"
                                        >
                                            Mark Wonderwall as Played
                                        </Button>
                                    </div>
                                </>
                            )}
                            
                            {tourStep === 4 && (
                                <>
                                    <h3 className="text-lg font-semibold mb-2">🔍 Step 5: Manual Song Search</h3>
                                    <p className="text-sm mb-3">You can also search and mark songs that weren't requested but you decided to play!</p>
                                    <div className="flex justify-center space-x-2">
                                        <Button 
                                            size="sm"
                                            onClick={() => setTourStep(5)}
                                            className="bg-white text-orange-600 hover:bg-gray-100"
                                        >
                                            Continue
                                        </Button>
                                    </div>
                                </>
                            )}
                            
                            {tourStep === 5 && (
                                <>
                                    <h3 className="text-lg font-semibold mb-2">📋 Step 6: Create Setlists</h3>
                                    <p className="text-sm mb-3">Setlists help you organize songs and display your selected Setlist to your audience. Create themed Setlists like "Wedding Songs", "Pub Classics", or "Acoustic Set" to quickly access and showcase your most popular songs!</p>
                                    <div className="bg-orange-900/20 p-3 rounded-lg border border-orange-700/50 mb-3">
                                        <div className="flex items-center space-x-2 mb-1">
                                            <ListMusic className="w-4 h-4 text-orange-400" />
                                            <span className="text-sm font-medium text-orange-200">Setlist Benefits:</span>
                                        </div>
                                        <ul className="text-xs text-orange-300 space-y-1">
                                            <li>• Selecting or switching to a setlist is updated to your audience in real-time</li>
                                            <li>• Organize songs by theme, genre or event type/venue</li>
                                            <li>• Have full control of your performance and atmosphere by switching to any setlist at any time</li>
                                        </ul>
                                    </div>
                                    <div className="flex justify-center space-x-2">
                                        <Button 
                                            size="sm"
                                            onClick={() => setTourStep(6)}
                                            className="bg-white text-blue-600 hover:bg-gray-100"
                                        >
                                            Continue
                                        </Button>
                                    </div>
                                </>
                            )}
                            
                            {tourStep === 6 && (
                                <>
                                    <h3 className="text-lg font-semibold mb-2">🏁 Step 7: End Your Session</h3>
                                    <p className="text-sm mb-3">When your performance is complete, click the toggle to go offline and see your session summary!</p>
                                    <div className="flex justify-center space-x-2">
                                        <Button 
                                            size="sm"
                                            onClick={() => setTourStep(7)}
                                            className="bg-white text-red-600 hover:bg-gray-100"
                                        >
                                            Complete Tour
                                        </Button>
                                    </div>
                                </>
                            )}
                            
                            {tourStep === 7 && (
                                <>
                                    <h3 className="text-lg font-semibold mb-2">🎉 Tour Complete!</h3>
                                    <p className="text-sm mb-3">You've learned how to use the Live Dashboard! You can now go live and manage your performances effectively.</p>
                                    <div className="bg-purple-900/20 p-3 rounded-lg border border-purple-700/50 mb-3">
                                        <div className="flex items-center space-x-2 mb-1">
                                            <MessageCircle className="w-4 h-4 text-purple-400" />
                                            <span className="text-sm font-medium text-purple-200">Need Help? Ask Melody!</span>
                                        </div>
                                        <p className="text-xs text-purple-300">
                                            Melody, your AI assistant, is available in the sidebar to help with any questions. 
                                            Just click the purple "Melody" button next to Logout for instant support!
                                        </p>
                                    </div>
                                    <div className="flex justify-center space-x-2">
                                        <Button 
                                            size="sm"
                                            onClick={() => {
                                                setShowTour(false);
                                                setTourStep(0);
                                                setTourData({
                                                    isLive: false,
                                                    showShareSection: true,
                                                    mockRequests: [],
                                                    mockTips: [],
                                                    sessionStartTime: null,
                                                    sessionEndTime: null,
                                                    timeLeft: '45:00'
                                                });
                                                
                                            }}
                                            className="bg-white text-blue-600 hover:bg-gray-100"
                                        >
                                            Add Your Songs
                                        </Button>
                                    </div>
                                </>
                            )}


                        </div>
                    </div>
                </div>
            )}
            
            {/* Song Wizard Overlay */}
            <AddSongWizard
                isVisible={showSongWizard}
                onComplete={handleSongWizardComplete}
                onDismiss={() => setShowSongWizard(false)}
            />
            
            {/* Trial Tip Popup */}
            <AnimatePresence>
                {showTrialTipPopup && trialTipData && (
                    <motion.div
                        initial={{ opacity: 0, scale: 0.8, y: 50 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.8, y: 50 }}
                        transition={{ type: 'spring', damping: 20, stiffness: 300 }}
                        className={`fixed z-[10000] ${mobile.isMobile ? 'inset-0 flex items-start justify-center px-4 pointer-events-none' : 'top-20 left-1/2 transform -translate-x-1/2'}`}
                        style={mobile.isMobile ? { paddingTop: 'calc(env(safe-area-inset-top, 0px) + 25%)', paddingBottom: 'env(safe-area-inset-bottom, 0px)' } : {}}
                    >
                        <motion.div
                            className={`${mobile.isMobile ? 'w-full max-w-xs pointer-events-auto' : 'min-w-[320px] max-w-md'}`}
                            initial={mobile.isMobile ? { scale: 0.9 } : {}}
                            animate={mobile.isMobile ? { scale: 1 } : {}}
                        >
                            <Card className="bg-gradient-to-r from-red-900/90 to-pink-900/90 border-2 border-red-500/60 shadow-2xl shadow-red-500/50">
                                <CardContent className={mobile.isMobile ? "p-4" : "p-6"}>
                                    <div className="flex items-start space-x-4">
                                        <div className="flex-shrink-0">
                                            <div className={`${mobile.isMobile ? 'w-12 h-12' : 'w-16 h-16'} bg-red-500/20 rounded-full flex items-center justify-center border-2 border-red-400/60`}>
                                                <Heart className={`${mobile.isMobile ? 'w-6 h-6' : 'w-8 h-8'} text-red-400 fill-red-400 animate-pulse`} />
                                            </div>
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center justify-between mb-2">
                                                <h3 className={`text-white font-bold ${mobile.isMobile ? 'text-sm leading-tight' : 'text-lg'}`}>You Received a Tip!</h3>
                                                <button
                                                    onClick={() => setShowTrialTipPopup(false)}
                                                    className="text-red-200 hover:text-white transition-colors flex-shrink-0 ml-2"
                                                >
                                                    <X className={`${mobile.isMobile ? 'w-4 h-4' : 'w-5 h-5'}`} />
                                                </button>
                                            </div>
                                            <div className="space-y-2">
                                                <div className="flex items-baseline space-x-2 flex-wrap">
                                                    <span className={`text-red-200 ${mobile.isMobile ? 'text-xs' : 'text-sm'}`}>from</span>
                                                    <span className={`text-white font-semibold ${mobile.isMobile ? 'text-sm' : 'text-lg'} break-words`}>{trialTipData.requester_name}</span>
                                                </div>
                                                <div className="flex items-baseline space-x-2">
                                                    <span className={`font-bold text-white ${mobile.isMobile ? 'text-xl' : 'text-3xl'}`}>{format(trialTipData.amount)}</span>
                                                </div>
                                                {trialTipData.message && (
                                                    <p className={`text-red-100 ${mobile.isMobile ? 'text-xs' : 'text-sm'} italic mt-2 break-words`}>"{trialTipData.message}"</p>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
            
            {/* Add Songs Required Modal */}
            <AddSongsRequiredModal
                isOpen={showAddSongsModal}
                onClose={() => setShowAddSongsModal(false)}
                onNavigateToCatalogue={() => {
                    setShowAddSongsModal(false);
                    navigate('/admin/catalogue');
                }}
            />
        </>
    );
};

export default LiveDashboardPage;